import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate, uploadFile, uploadFileMethod, downloadFile } from '../shared/api';
import { API_BASE } from '../shared/constants';
import { formatDate } from '../shared/formatting';
import { QuickSearchInput, matchesQuickSearch } from '../shared/quickSearch';
import { MultiSelectDropdown } from '../shared/MultiSelectDropdown';
import { RichTextEditor } from '../shared/RichTextEditor';
import { DateInput } from '../shared/DateInput';
import { Crumbs, Tabs, Card, Btn } from '../shared/ui';
import { readUrlParam, useUrlSync } from '../shared/urlState';
import type { BiAdminUser } from '../types/biApp';
import { CircularQuizPanel, EmployeeReadsList, EmployeeReportView, WeeklyDigestModal, type EmployeeRead } from './circulars/learning';

type Locale = 'hr' | 'en';

type CircularRow = {
    id: number;
    public_id: string;
    subject: string;
    status: 'draft' | 'sending' | 'sent' | 'failed';
    sent_at: string | null;
    recipient_count: number;
    opened_count: number;
    created_at: string;
    sender_name: string | null;
    sender_email: string | null;
    version_number?: number;
    is_latest?: boolean;
    thread_id?: number | null;
    parent_circular_id?: number | null;
    approval_status?: string | null;
    prioritet?: string | null;
    group_ids?: number[];
    is_mine?: boolean;              // ja sam pošiljatelj → vidim je u svakom statusu
    awaiting_my_approval?: boolean; // aktivan token na moje ime → na meni je red
    circular_number?: string | null;
    attachment_number?: string | null;
    attachment_name?: string | null;
    sektor?: string | null;
    odjel?: string | null;
    vrsta?: string | null;
};

type CircularRecipient = {
    id: number;
    email_address: string;
    status: 'pending' | 'sent' | 'failed';
    sent_at: string | null;
    opened_at: string | null;
    user_id: number | null;
    user_name: string | null;
    source_direct?: boolean;
    source_group_ids?: number[];
    source_groups?: Array<{ id: number; name: string }>;
    employee_reads?: EmployeeRead[];
};

type ResolvedRecipientPreview = {
    email: string;
    name: string | null;
    user_id: number | null;
    source_direct: boolean;
    source_groups?: Array<{ id: number; name: string }>;
};

function recipientSourceText(r: { source_direct?: boolean; source_groups?: Array<{ name: string }> }, locale: Locale): string {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const parts = (r.source_groups ?? []).map((g) => g.name).filter(Boolean);
    if (r.source_direct) parts.push(t('osobno', 'direct'));
    return parts.join(' · ');
}

type ThreadEntry = {
    id: number;
    subject: string;
    parent_circular_id: number | null;
    message_id: string | null;
    sent_at: string | null;
    created_at: string;
    inbound_source: 'composed' | 'imap_reply';
    recipient_count: number;
    opened_count: number;
    sender_name: string | null;
    sender_email: string | null;
};

type CircularDetail = {
    circular: {
        id: number;
        subject: string;
        html_body: string;
        status: string;
        sent_at: string | null;
        recipient_count: number;
        opened_count: number;
        created_at: string;
        message_id: string | null;
        parent_circular_id: number | null;
        thread_id: number | null;
        inbound_source: string;
        approval_status?: string | null;
        ready_to_send?: boolean | number | string | null;
        attachment_name?: string | null;
        docdoc_url?: string | null;
        docdoc_uuid?: string | null;
    };
    recipients: CircularRecipient[];
    thread: ThreadEntry[];
    approvers?: Array<{ approver_zaposlenik_id?: number | null; approver_name: string | null; approver_email: string | null; status: string; used_action: string | null; used_at: string | null }>;
    events?: Array<{ actor_name: string | null; action: string; comment: string | null; created_at: string | null }>;
    pending_recipient_count?: number;
    permissions?: { can_edit?: boolean; can_manage_approvers?: boolean };
};

type Group = {
    id: number;
    public_id: string;
    parent_group_id: number | null;
    name: string;
    description: string | null;
    color: string | null;
    direct_member_count: number;
    created_at: string;
};

type ReadReportRow = { email: string; name: string | null; total: number; read: number; unread: number; unread_list: Array<{ id: number; label: string; sent_at: string | null }> };

type ReportSelection = { id: number; name: string; emails: string[] };

/** Izvještaj čitanosti okružnica (Iva): tko NIJE pročitao, po primatelju, od zadanog datuma. */
function ReadReportModal({ token, locale, onClose }: { token: string; locale: Locale; onClose: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [dateFrom, setDateFrom] = useState('');
    const [data, setData] = useState<{ recipients: ReadReportRow[]; with_unread: number; recipient_count: number } | null>(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [onlyUnread, setOnlyUnread] = useState(true);
    const [mode, setMode] = useState<'address' | 'employee'>('address');
    // Checkbox izbor tko ulazi u izvještaj + spremljene selekcije.
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [saved, setSaved] = useState<ReportSelection[]>([]);
    const [selName, setSelName] = useState('');
    const load = useCallback(async () => {
        setBusy(true); setErr('');
        try {
            const qs = dateFrom ? `?date_from=${encodeURIComponent(dateFrom)}` : '';
            const d = await api<{ recipients: ReadReportRow[]; with_unread: number; recipient_count: number }>(`/circulars/read-report${qs}`, token);
            setData(d);
            setSelected(new Set((d.recipients ?? []).map((r) => r.email.toLowerCase())));   // default: svi uključeni
        } catch (e) { setErr(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    }, [dateFrom, token]);
    useEffect(() => { void load(); }, [load]);
    const loadSaved = useCallback(async () => {
        try { setSaved((await api<{ rows: ReportSelection[] }>('/circulars/report-selections', token)).rows ?? []); } catch { /* */ }
    }, [token]);
    useEffect(() => { void loadSaved(); }, [loadSaved]);

    const rows = (data?.recipients ?? []).filter((r) => !onlyUnread || r.unread > 0);
    const isSel = (email: string) => selected.has(email.toLowerCase());
    const toggle = (email: string) => setSelected((s) => { const n = new Set(s); const k = email.toLowerCase(); n.has(k) ? n.delete(k) : n.add(k); return n; });
    const allShownSelected = rows.length > 0 && rows.every((r) => isSel(r.email));
    const toggleAll = () => setSelected((s) => { const n = new Set(s); if (allShownSelected) rows.forEach((r) => n.delete(r.email.toLowerCase())); else rows.forEach((r) => n.add(r.email.toLowerCase())); return n; });
    const selCount = selected.size;

    const exportXls = async () => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('date_from', dateFrom);
        if (onlyUnread) p.set('only_unread', '1');
        [...selected].forEach((e) => p.append('emails[]', e));
        await downloadFile(`/circulars/read-report/export?${p.toString()}`, token, `okruznice_citanost_${dateFrom || 'sve'}.xlsx`);
    };
    const applySaved = (sel: ReportSelection | null) => { if (sel) setSelected(new Set(sel.emails.map((e) => e.toLowerCase()))); };
    const saveSelection = async () => {
        const name = selName.trim();
        if (!name) { alert(t('Upiši ime selekcije.', 'Enter a selection name.')); return; }
        if (selected.size === 0) { alert(t('Odaberi barem jednog primatelja.', 'Select at least one recipient.')); return; }
        try { await mutate('/circulars/report-selections', token, 'POST', { name, emails: [...selected] }); setSelName(''); await loadSaved(); }
        catch (e) { alert(e instanceof Error ? e.message : 'API'); }
    };
    const deleteSelection = async (id: number) => {
        if (!confirm(t('Obrisati spremljenu selekciju?', 'Delete saved selection?'))) return;
        try { await mutate(`/circulars/report-selections/${id}`, token, 'DELETE'); await loadSaved(); }
        catch (e) { alert(e instanceof Error ? e.message : 'API'); }
    };

    return (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 12px', zIndex: 1000, overflowY: 'auto' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(920px, 97vw)', background: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 20px 60px rgba(107,79,28,0.3)' }}>
                <div style={{ background: 'linear-gradient(135deg,var(--ad-ink),var(--ad-acc))', color: '#fffbeb', padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong>📊 {t('Izvještaj čitanosti okružnica', 'Circular read report')}</strong>
                    <button type="button" onClick={onClose} style={{ background: 'transparent', border: 0, color: '#fffbeb', fontSize: '1.6rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
                </div>
                <div style={{ padding: '14px 20px', maxHeight: '80vh', overflowY: 'auto' }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                        <label style={{ fontSize: '0.86rem', display: 'flex', alignItems: 'center', gap: 6 }}>{t('Od datuma', 'From date')}:
                            <DateInput value={dateFrom} onChange={setDateFrom} /></label>
                        <label style={{ fontSize: '0.86rem', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                            <input type="checkbox" checked={onlyUnread} onChange={(e) => setOnlyUnread(e.target.checked)} /> {t('samo s nepročitanima', 'only with unread')}</label>
                        <div style={{ flex: 1 }} />
                        <Btn variant="soft" size="sm" onClick={() => void exportXls()} disabled={selCount === 0}>📥 {t('Izvoz (XLS)', 'Export (XLS)')} ({selCount})</Btn>
                    </div>
                    {/* Spremljene selekcije primatelja (tko ulazi u izvještaj) */}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10, padding: '8px 10px', background: '#faf7ef', borderRadius: 8, fontSize: '0.82rem' }}>
                        <strong>👥 {t('Uključeni', 'Included')}: {selCount}/{data?.recipient_count ?? 0}</strong>
                        <span style={{ color: '#bbb' }}>·</span>
                        <label>{t('Učitaj selekciju', 'Load selection')}:
                            <select value="" onChange={(e) => { const s = saved.find((x) => String(x.id) === e.target.value); applySaved(s ?? null); }} style={{ marginLeft: 4, fontSize: '0.8rem', padding: '2px 4px' }}>
                                <option value="">{t('— odaberi —', '— pick —')}</option>
                                {saved.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.emails.length})</option>)}
                            </select>
                        </label>
                        {saved.length > 0 ? (
                            <select value="" onChange={(e) => { if (e.target.value) void deleteSelection(Number(e.target.value)); }} style={{ fontSize: '0.8rem', padding: '2px 4px' }} title={t('Obriši spremljenu', 'Delete saved')}>
                                <option value="">🗑 {t('obriši…', 'delete…')}</option>
                                {saved.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        ) : null}
                        <div style={{ flex: 1 }} />
                        <input type="text" value={selName} onChange={(e) => setSelName(e.target.value)} placeholder={t('Ime selekcije…', 'Selection name…')} style={{ fontSize: '0.8rem', padding: '3px 8px', width: 150 }} />
                        <Btn variant="soft" size="sm" onClick={() => void saveSelection()}>💾 {t('Spremi selekciju', 'Save selection')}</Btn>
                    </div>
                    <div style={{ display: 'flex', gap: 6, margin: '4px 0 8px' }}>
                        <Btn variant={mode === 'address' ? 'primary' : 'ghost'} size="sm" onClick={() => setMode('address')}>{t('Po adresi (poslovnici)', 'By address (branch)')}</Btn>
                        <Btn variant={mode === 'employee' ? 'primary' : 'ghost'} size="sm" onClick={() => setMode('employee')}>{t('Po djelatniku (PIN, ispit)', 'By employee (PIN, quiz)')}</Btn>
                    </div>
                    {mode === 'employee' ? <EmployeeReportView token={token} locale={locale} dateFrom={dateFrom} /> : (<>
                    {err ? <p className="error">{err}</p> : null}
                    {busy ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}
                    {data ? <p className="muted" style={{ fontSize: '0.82rem' }}>{data.recipient_count} {t('primatelja', 'recipients')} · <strong style={{ color: '#b14040' }}>{data.with_unread}</strong> {t('ima nepročitanih', 'have unread')}{dateFrom ? ` · ${t('od', 'from')} ${dateFrom.split('-').reverse().join('.')}.` : ''}</p> : null}
                    {!busy && rows.length === 0 ? <p className="muted" style={{ fontStyle: 'italic' }}>{t('Nema podataka (svi su pročitali ili nema poslanih okružnica).', 'No data (all read or no sent circulars).')}</p> : null}
                    {rows.length > 0 ? (
                        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.82rem' }}>
                            <thead><tr style={{ borderBottom: '2px solid #efe6d2', textAlign: 'left' }}>
                                <th style={{ padding: '6px 8px', width: 28 }} title={t('Označi sve / ništa', 'Select all / none')}>
                                    <input type="checkbox" checked={allShownSelected} onChange={toggleAll} />
                                </th>
                                <th style={{ padding: '6px 8px' }}>{t('Primatelj', 'Recipient')}</th>
                                <th style={{ padding: '6px 8px', textAlign: 'right' }}>{t('Poslano', 'Sent')}</th>
                                <th style={{ padding: '6px 8px', textAlign: 'right' }}>{t('Pročitano', 'Read')}</th>
                                <th style={{ padding: '6px 8px', textAlign: 'right' }}>{t('Nepročitano', 'Unread')}</th>
                                <th style={{ padding: '6px 8px' }}>{t('Nepročitane okružnice', 'Unread circulars')}</th>
                            </tr></thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.email} style={{ borderBottom: '1px solid #f3ecdb', background: !isSel(r.email) ? 'rgba(0,0,0,0.03)' : (r.unread > 0 ? 'rgba(177,64,64,0.04)' : undefined), opacity: isSel(r.email) ? 1 : 0.55 }}>
                                        <td style={{ padding: '5px 8px', textAlign: 'center' }}><input type="checkbox" checked={isSel(r.email)} onChange={() => toggle(r.email)} /></td>
                                        <td style={{ padding: '5px 8px' }}><strong>{r.name || r.email.split('@')[0]}</strong><br /><span className="muted" style={{ fontSize: '0.72rem' }}>{r.email}</span></td>
                                        <td style={{ padding: '5px 8px', textAlign: 'right' }}>{r.total}</td>
                                        <td style={{ padding: '5px 8px', textAlign: 'right', color: '#1f7a36' }}>{r.read}</td>
                                        <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700, color: r.unread > 0 ? '#b14040' : '#999' }}>{r.unread}</td>
                                        <td style={{ padding: '5px 8px', fontSize: '0.74rem', color: 'rgba(0,0,0,0.6)' }}>{r.unread_list.map((u) => u.label).join(' · ') || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : null}
                    </>)}
                </div>
            </div>
        </div>
    );
}

export function CircularsModule({ token, locale, isSuperadmin = false, canCreate = false, isCircularManager = false, canManageGroups = false, canSeeReadReport = false }: { token: string; locale: Locale; isSuperadmin?: boolean; canCreate?: boolean; isCircularManager?: boolean; canManageGroups?: boolean; canSeeReadReport?: boolean }) {
    // "Izvještaj čitanosti" gumb/tab: NE isti gate kao isCircularManager (to je uređivanje/uklanjanje
    // odobravatelja tuđih okružnica, superadmin + Iva Breulj). Backend GET /circulars/read-report
    // (assertCanSeeReadReport) pušta administrator/manager role + imenovane voditeljice bez te role
    // (Renata Kalinkov UID 705, 23.09.2026) — gumb mora pratiti ISTI gate, inače podatak postoji ali
    // sučelje do njega nema put.
    const mayViewReadReport = isSuperadmin || isCircularManager || canSeeReadReport;
    // canCreate = superadmin || explicit per-user flag (2026-06-03 Iva)
    const mayCompose = isSuperadmin || canCreate;
    // canManageGroups = superadmin || explicit per-user flag (2026-08 Tomislav/Mira — kreiranje grupa primatelja)
    const mayManageGroups = isSuperadmin || canManageGroups;
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [view, setView] = useState<'list' | 'compose' | 'groups'>(readUrlParam('tab', 'list') as 'list' | 'compose' | 'groups');
    useUrlSync('tab', view, view === 'list');
    const [showReadReport, setShowReadReport] = useState(false);
    const [showWeekly, setShowWeekly] = useState(false);
    const [rows, setRows] = useState<CircularRow[]>([]);
    const [groups, setGroups] = useState<Group[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [detailId, setDetailId] = useState<number | null>(null);
    const [detail, setDetail] = useState<CircularDetail | null>(null);
    const [replyTo, setReplyTo] = useState<{ parentId: number; subject: string } | null>(null);
    const [editingDraftId, setEditingDraftId] = useState<number | null>(null);
    const [showTrashed, setShowTrashed] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<number | 'all' | 'ungrouped'>('all');

    const loadAll = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const r1 = await api<{ rows: CircularRow[] }>(showTrashed ? '/circulars?trashed=1' : '/circulars', token);
            setRows(r1.rows);
            try {
                const r2 = await api<{ rows: Group[] }>('/circular-groups', token);
                setGroups(r2.rows.map((g) => ({
                    ...g,
                    id: Number(g.id),
                    parent_group_id: g.parent_group_id == null ? null : Number(g.parent_group_id),
                })));
            } catch { setGroups([]); }
        } catch (e) { setError(e instanceof Error ? e.message : 'API'); }
        finally { setLoading(false); }
    }, [token, showTrashed]);

    useEffect(() => { void loadAll(); }, [loadAll]);

    const restoreCircular = useCallback(async (id: number, scope: 'version' | 'thread') => {
        try { await mutate(`/circulars/${id}/restore`, token, 'POST', { scope }); await loadAll(); }
        catch (e) { alert(e instanceof Error ? e.message : 'API'); }
    }, [token, loadAll]);

    useEffect(() => {
        const p = new URLSearchParams(window.location.search);
        const c = p.get('circular');
        if (c && /^\d+$/.test(c)) setDetailId(Number(c));
    }, []);

    useEffect(() => {
        if (detailId == null) { setDetail(null); return; }
        void api<CircularDetail>(`/circulars/${detailId}`, token).then(setDetail).catch((e) => setError(e instanceof Error ? e.message : 'API'));
    }, [detailId, token]);

    const tabCls = (active: boolean) => 'okr-tab' + (active ? ' is-active' : '');

    // Stablo distribucijskih lista: count po listi je EGZAKTAN (kao m.31 doc_count), a klik na
    // roditelja filtrira self + sve potomke (rollup) — usklađeno s m.31 ponašanjem.
    const childrenByParent = useMemo(() => {
        const m = new Map<number | null, Group[]>();
        for (const g of groups) {
            const p = g.parent_group_id ?? null;
            if (!m.has(p)) m.set(p, []);
            m.get(p)!.push(g);
        }
        return m;
    }, [groups]);

    const descendantIds = useCallback((rootId: number): number[] => {
        const acc: number[] = [rootId];
        const stack = [rootId];
        while (stack.length) {
            const cur = stack.pop()!;
            for (const ch of childrenByParent.get(cur) ?? []) { acc.push(ch.id); stack.push(ch.id); }
        }
        return acc;
    }, [childrenByParent]);

    // Rolled-up broj po listi: distinct okružnice te liste + svih pod-lista (usklađeno s klik-filterom).
    const countByGroup = useMemo(() => {
        const m = new Map<number, number>();
        for (const g of groups) {
            const ids = new Set(descendantIds(g.id));
            m.set(g.id, rows.filter((r) => (r.group_ids ?? []).some((x) => ids.has(x))).length);
        }
        return m;
    }, [groups, rows, descendantIds]);
    const ungroupedCount = useMemo(() => rows.filter((r) => (r.group_ids ?? []).length === 0).length, [rows]);

    const filteredRows = useMemo(() => {
        if (selectedGroup === 'all') return rows;
        if (selectedGroup === 'ungrouped') return rows.filter((r) => (r.group_ids ?? []).length === 0);
        const ids = new Set(descendantIds(selectedGroup));
        return rows.filter((r) => (r.group_ids ?? []).some((g) => ids.has(g)));
    }, [rows, selectedGroup, descendantIds]);

    // Stablo se prikazuje samo superadminu (grupe se fetchaju samo njemu) i izvan Smeća.
    const showTree = isSuperadmin && groups.length > 0 && !showTrashed;

    const isHrms = typeof document !== 'undefined' && document.body.dataset.theme === 'hrms';

    // hrms tabovi: navigacija (list/compose/groups); Smeće + Osvježi su akcije (Btn).
    const hrmsTabs = [
        { key: 'list', label: `${t('Okružnice', 'Circulars')} (${showTrashed ? '…' : rows.length})`, icon: '📋' },
        ...(mayCompose ? [{ key: 'compose', label: t('Nova okružnica', 'New circular'), icon: '✏️' }] : []),
        ...(mayManageGroups ? [{ key: 'groups', label: `${t('Grupe', 'Groups')} (${groups.length})`, icon: '👥' }] : []),
    ];
    const hrmsActiveTab = view === 'compose' ? 'compose' : view === 'groups' ? 'groups' : 'list';

    return (
        <section className={isHrms ? 'okr-wrap' : 'card admin-block okr-wrap'} style={isHrms ? { width: '100%' } : undefined}>
            {isHrms ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 13, marginBottom: 16 }}>
                    <Crumbs items={[t('Dashboard', 'Dashboard'), t('Okružnice', 'Circulars')]} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <Tabs
                            tabs={hrmsTabs}
                            active={hrmsActiveTab}
                            onChange={(k) => {
                                if (k === 'compose') { setShowTrashed(false); setView('compose'); setReplyTo(null); setEditingDraftId(null); }
                                else if (k === 'groups') { setShowTrashed(false); setView('groups'); }
                                else { setShowTrashed(false); setView('list'); }
                            }}
                        />
                        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', alignItems: 'center' }}>
                            {isSuperadmin ? (
                                <Btn variant={showTrashed ? 'primary' : 'ghost'} size="sm" onClick={() => { setShowTrashed((s) => !s); setView('list'); }}>
                                    🗑 {showTrashed ? t('Smeće ●', 'Trash ●') : t('Smeće', 'Trash')}
                                </Btn>
                            ) : null}
                            <Btn variant="ghost" size="sm" onClick={() => void loadAll()}>🔄 {t('Osvježi', 'Refresh')}</Btn>
                            {mayViewReadReport ? <Btn variant="ghost" size="sm" onClick={() => setShowReadReport(true)}>📊 {t('Izvještaj čitanosti', 'Read report')}</Btn> : null}
                            {mayViewReadReport ? <Btn variant="ghost" size="sm" onClick={() => setShowWeekly(true)}>🗓 {t('Tjedni pregled', 'Weekly')}</Btn> : null}
                        </div>
                    </div>
                    {error ? <p className="error" style={{ color: '#dc2626', margin: 0 }}>{error}</p> : null}
                </div>
            ) : (
            <>
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">📨</div>
                <div className="bi-module-hero-text">
                    <h1>{t('Okružnice', 'Circulars')}</h1>
                    <p className="muted">
                        {t(
                            'Interne komunikacije prema timu i grupama. Threading + read tracking preko "Otvori u BIS-u" linka i inbound IMAP-a (replies stižu u thread).',
                            'Internal communications to team and groups. Threading + read tracking via "Open in BIS" link and inbound IMAP (replies into thread).',
                        )}
                    </p>
                </div>
            </div>

            {error ? <p className="error">{error}</p> : null}

            <div className="okr-tabs">
                <button type="button" onClick={() => { setShowTrashed(false); setView('list'); }} className={tabCls(view === 'list' && !showTrashed)}>
                    📋 {t('Okružnice', 'Circulars')} ({showTrashed ? '…' : rows.length})
                </button>
                {mayCompose ? (
                    <button type="button" onClick={() => { setView('compose'); setReplyTo(null); setEditingDraftId(null); }} className={tabCls(view === 'compose')}>
                        ✏️ {t('Nova okružnica', 'New circular')}
                    </button>
                ) : null}
                {mayManageGroups ? (
                    <button type="button" onClick={() => setView('groups')} className={tabCls(view === 'groups')}>
                        👥 {t('Grupe', 'Groups')} ({groups.length})
                    </button>
                ) : null}
                {isSuperadmin ? (
                    <button type="button" onClick={() => { setShowTrashed((s) => !s); setView('list'); }} className={tabCls(showTrashed) + ' is-ghost'}>
                        🗑 {showTrashed ? t('Smeće ●', 'Trash ●') : t('Smeće', 'Trash')}
                    </button>
                ) : null}
                <button type="button" onClick={() => void loadAll()} className={'okr-tab' + (isSuperadmin ? '' : ' is-ghost')}>
                    🔄 {t('Osvježi', 'Refresh')}
                </button>
                {mayViewReadReport ? (
                    <button type="button" onClick={() => setShowReadReport(true)} className="okr-tab is-ghost">
                        📊 {t('Izvještaj čitanosti', 'Read report')}
                    </button>
                ) : null}
                {mayViewReadReport ? (
                    <button type="button" onClick={() => setShowWeekly(true)} className="okr-tab is-ghost">
                        🗓 {t('Tjedni pregled', 'Weekly')}
                    </button>
                ) : null}
            </div>
            </>
            )}

            {showReadReport ? <ReadReportModal token={token} locale={locale} onClose={() => setShowReadReport(false)} /> : null}
            {showWeekly ? <WeeklyDigestModal token={token} locale={locale} onClose={() => setShowWeekly(false)} /> : null}

            {view === 'list' ? (
                loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> :
                    rows.length === 0 ? <p className="muted" style={{ fontStyle: 'italic' }}>{showTrashed ? t('Smeće je prazno.', 'Trash is empty.') : t('Još nema poslanih okružnica.', 'No circulars yet.')}</p> :
                        showTree ? (
                            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) 1fr', gap: '1rem', alignItems: 'start' }}>
                                <CircularGroupTree
                                    groups={groups}
                                    selected={selectedGroup}
                                    onSelect={setSelectedGroup}
                                    countByGroup={countByGroup}
                                    allCount={rows.length}
                                    ungroupedCount={ungroupedCount}
                                    locale={locale}
                                />
                                <div>
                                    {filteredRows.length === 0
                                        ? <p className="muted" style={{ fontStyle: 'italic' }}>{t('Nema okružnica u ovoj listi.', 'No circulars in this list.')}</p>
                                        : <CircularsList rows={filteredRows} locale={locale} onOpen={(id) => setDetailId(id)} trashed={false} onRestore={restoreCircular} />}
                                </div>
                            </div>
                        ) : (
                            <CircularsList rows={rows} locale={locale} onOpen={(id) => setDetailId(id)} trashed={showTrashed} onRestore={restoreCircular} />
                        )
            ) : null}
            {view === 'groups' ? (
                <GroupsAdmin token={token} locale={locale} groups={groups} onReload={() => void loadAll()} />
            ) : null}
            {view === 'compose' && mayCompose ? (
                <CircularCompose
                    token={token}
                    locale={locale}
                    groups={groups}
                    replyTo={replyTo}
                    draftId={editingDraftId}
                    key={editingDraftId ?? 'new'}
                    onSent={() => { setView('list'); setReplyTo(null); setEditingDraftId(null); void loadAll(); }}
                />
            ) : null}

            {detailId != null ? (
                <CircularDetailDrawer
                    detail={detail}
                    groups={groups}
                    locale={locale}
                    token={token}
                    mayCompose={mayCompose}
                    onClose={() => { setDetailId(null); setDetail(null); }}
                    onReply={(parentId, subject) => {
                        setReplyTo({ parentId, subject });
                        setEditingDraftId(null);
                        setView('compose');
                        setDetailId(null);
                    }}
                    onEditDraft={(id) => {
                        setReplyTo(null);
                        setEditingDraftId(id);
                        setView('compose');
                        setDetailId(null);
                        setDetail(null);
                    }}
                    onOpenThread={(id) => setDetailId(id)}
                    onChanged={() => { void loadAll(); }}
                    onOpenId={(id) => setDetailId(id)}
                />
            ) : null}
        </section>
    );
}

function CircularsList({ rows, locale, onOpen, trashed = false, onRestore }: { rows: CircularRow[]; locale: Locale; onOpen: (id: number) => void; trashed?: boolean; onRestore?: (id: number, scope: 'version' | 'thread') => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const isHrms = typeof document !== 'undefined' && document.body.dataset.theme === 'hrms';

    // Nedovršeno na MENI: moja okružnica koja nije poslana i ne čeka nikoga drugog (skica, vraćena na izmjene,
    // odbačena, ili odobrena a nije poslana). Bez ovog popisa okružnica postoji samo u mailu i ostane visjeti.
    const myTodo = trashed ? [] : rows.filter((r) => r.is_mine && r.status !== 'sent'
        && ['draft', 'changes_requested', 'revision_requested', 'rejected', 'approved'].includes(String(r.approval_status ?? 'draft')));
    const toApprove = trashed ? [] : rows.filter((r) => r.awaiting_my_approval);

    const banner = (myTodo.length > 0 || toApprove.length > 0) ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {toApprove.length > 0 ? (
                <button type="button" onClick={() => onOpen(toApprove[0].id)}
                    style={{ background: 'rgba(177,64,64,0.1)', border: '1.5px solid rgba(177,64,64,0.45)', color: '#b14040', borderRadius: 8, padding: '7px 13px', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                    ✋ {t(`Čeka tvoje odobrenje: ${toApprove.length}`, `Awaiting your approval: ${toApprove.length}`)}
                </button>
            ) : null}
            {myTodo.length > 0 ? (
                <button type="button" onClick={() => onOpen(myTodo[0].id)}
                    style={{ background: 'rgba(138,104,23,0.12)', border: '1.5px solid rgba(138,104,23,0.45)', color: 'var(--ad-acc-3)', borderRadius: 8, padding: '7px 13px', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                    📋 {t(`Tvoje nedovršeno: ${myTodo.length}`, `Your unfinished: ${myTodo.length}`)}
                </button>
            ) : null}
        </div>
    ) : null;

    const tableInner = (
        <div className="okr-card" style={{ padding: 0, overflow: 'hidden', ...(isHrms ? { border: 'none', boxShadow: 'none', background: 'transparent' } : {}) }}>
            <table className="okr-list">
                <thead>
                    <tr>
                        <th>{t('Naslov', 'Subject')}</th>
                        <th>{t('Status', 'Status')}</th>
                        <th>{t('Poslano', 'Sent')}</th>
                        <th style={{ textAlign: 'right' }}>{t('Primatelji', 'Recipients')}</th>
                        <th style={{ textAlign: 'right' }}>{t('Otvoreno', 'Opened')}</th>
                        <th>{t('Pošiljatelj', 'Sender')}</th>
                        {trashed ? <th /> : null}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => {
                        const openPct = r.recipient_count > 0 ? (100 * r.opened_count / r.recipient_count) : 0;
                        return (
                            <tr key={r.id} onClick={() => trashed ? undefined : onOpen(r.id)} style={{ cursor: trashed ? 'default' : 'pointer', opacity: r.is_latest === false ? 0.55 : 1, boxShadow: r.awaiting_my_approval ? 'inset 3px 0 0 #b14040' : (r.is_mine && r.status !== 'sent' ? 'inset 3px 0 0 var(--ad-acc-3)' : undefined) }}>
                                <td>
                                    {r.circular_number ? <span style={{ display: 'block', fontFamily: 'ui-monospace, monospace', fontSize: '0.7rem', color: 'var(--ad-acc-3)', fontWeight: 700 }}>{r.circular_number}{r.attachment_number ? ` · 📎 ${r.attachment_number}` : ''}</span> : null}
                                    {(r.version_number ?? 1) > 1 || r.is_latest === false ? (
                                        <span style={{ background: 'rgba(107,79,28,0.12)', color: 'var(--ad-ink)', padding: '1px 6px', borderRadius: 7, fontSize: '0.7rem', fontWeight: 700, marginRight: 6 }} title={r.is_latest === false ? t('starija verzija', 'older version') : t('najnovija verzija', 'latest version')}>
                                            v{r.version_number ?? 1}{r.is_latest === false ? '' : ' ●'}
                                        </span>
                                    ) : null}
                                    <strong>{r.subject}</strong>
                                    {r.prioritet ? <span style={{ marginLeft: 6, background: 'rgba(177,64,64,0.1)', color: '#b14040', padding: '1px 6px', borderRadius: 7, fontSize: '0.68rem', fontWeight: 700 }}>{r.prioritet}</span> : null}
                                </td>
                                <td>
                                    {r.status !== 'sent' && r.approval_status === 'draft' ? <span style={{ background: 'rgba(120,113,90,0.15)', color: '#6b5f3a', padding: '3px 9px', borderRadius: 7, fontSize: '0.72rem', fontWeight: 700 }}>💾 {t('skica', 'draft')}</span>
                                    : r.status !== 'sent' && r.approval_status === 'pending' ? <span style={{ background: 'rgba(138,104,23,0.15)', color: 'var(--ad-acc-3)', padding: '3px 9px', borderRadius: 7, fontSize: '0.72rem', fontWeight: 700 }}>⏳ {t('čeka odobrenje', 'pending approval')}</span>
                                    : r.status !== 'sent' && (r.approval_status === 'changes_requested' || r.approval_status === 'revision_requested') ? <span style={{ background: 'rgba(138,104,23,0.15)', color: 'var(--ad-acc-3)', padding: '3px 9px', borderRadius: 7, fontSize: '0.72rem', fontWeight: 700 }}>✎ {t('izmjene', 'changes')}</span>
                                    : r.status !== 'sent' && r.approval_status === 'rejected' ? <span style={{ background: 'rgba(177,64,64,0.15)', color: '#b14040', padding: '3px 9px', borderRadius: 7, fontSize: '0.72rem', fontWeight: 700 }}>✗ {t('odbačeno', 'rejected')}</span>
                                    : r.status !== 'sent' && r.approval_status === 'approved' ? <span style={{ background: 'rgba(31,122,54,0.12)', color: '#1f7a36', padding: '3px 9px', borderRadius: 7, fontSize: '0.72rem', fontWeight: 700 }}>✓ {t('odobreno', 'approved')}</span>
                                    : (
                                    <span style={{
                                        background: r.status === 'sent' ? 'rgba(31,122,54,0.15)' : r.status === 'failed' ? 'rgba(177,64,64,0.15)' : 'rgba(var(--ad-acc-rgb),0.15)',
                                        color: r.status === 'sent' ? '#1f7a36' : r.status === 'failed' ? '#b14040' : 'var(--ad-acc-3)',
                                        padding: '3px 9px', borderRadius: 7, fontSize: '0.74rem', fontWeight: 700,
                                    }}>{r.status}</span>
                                    )}
                                </td>
                                <td>{r.sent_at ? formatDate(r.sent_at, locale) : '—'}</td>
                                <td style={{ textAlign: 'right', fontWeight: 600 }}>{r.recipient_count}</td>
                                <td style={{ textAlign: 'right' }}>
                                    <strong>{r.opened_count}</strong>
                                    <span className="muted" style={{ fontSize: '0.72rem', marginLeft: 4 }}>({openPct.toFixed(0)}%)</span>
                                </td>
                                <td className="muted" style={{ fontSize: '0.78rem' }}>{r.sender_name ?? r.sender_email ?? '—'}</td>
                                {trashed ? (
                                    <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                                        <button type="button" className="secondary compact" style={{ fontSize: '0.72rem', color: '#1f7a36' }} onClick={() => onRestore?.(r.id, 'version')}>↩ {t('Vrati', 'Restore')}</button>
                                        <button type="button" className="secondary compact" style={{ fontSize: '0.72rem', color: '#1f7a36', marginLeft: 4 }} onClick={() => onRestore?.(r.id, 'thread')}>↩ {t('Vrati thread', 'Restore thread')}</button>
                                    </td>
                                ) : null}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
    return isHrms
        ? <>{banner}<Card pad={0}>{tableInner}</Card></>
        : <>{banner}{tableInner}</>;
}

// Stablo distribucijskih lista (folderi po grupama) — mirror m.31 CategoryTreePanel.
// Badge = egzaktan broj okružnica te liste; klik filtrira self + potomke.
function CircularGroupTree({ groups, selected, onSelect, countByGroup, allCount, ungroupedCount, locale }: {
    groups: Group[];
    selected: number | 'all' | 'ungrouped';
    onSelect: (v: number | 'all' | 'ungrouped') => void;
    countByGroup: Map<number, number>;
    allCount: number;
    ungroupedCount: number;
    locale: Locale;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [expanded, setExpanded] = useState<Set<number>>(() => new Set());

    const childrenOf = (parent: number | null) => groups.filter((g) => (g.parent_group_id ?? null) === parent);

    const toggleExpand = (id: number) => {
        setExpanded((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
    };

    const renderNode = (node: Group, level: number): React.ReactNode => {
        const children = childrenOf(node.id);
        const hasChildren = children.length > 0;
        const isOpen = expanded.has(node.id);
        const isSelected = selected === node.id;
        return (
            <div key={node.id}>
                <div
                    onClick={() => onSelect(node.id)}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '4px 6px', paddingLeft: 6 + level * 14,
                        background: isSelected ? 'linear-gradient(135deg, #fef3c7, rgba(254,243,199,0.5))' : 'transparent',
                        borderLeft: isSelected ? '3px solid var(--ad-acc)' : '3px solid transparent',
                        borderRadius: 6, cursor: 'pointer', fontSize: '0.86rem', color: 'var(--ad-ink)',
                    }}
                >
                    {hasChildren ? (
                        <button type="button" onClick={(e) => { e.stopPropagation(); toggleExpand(node.id); }} style={{ background: 'transparent', border: 0, cursor: 'pointer', padding: '0 4px', color: 'var(--ad-ink)' }}>
                            {isOpen ? '▾' : '▸'}
                        </button>
                    ) : <span style={{ width: 16, display: 'inline-block' }} />}
                    <span style={{ flex: 1, fontWeight: isSelected ? 700 : 500 }}>📁 {node.name}</span>
                    <span style={{ fontSize: '0.72rem', color: 'rgba(0,0,0,0.5)' }}>{countByGroup.get(node.id) ?? 0}</span>
                </div>
                {hasChildren && isOpen ? children.map((c) => renderNode(c, level + 1)) : null}
            </div>
        );
    };

    const roots = childrenOf(null);

    return (
        <aside style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.22)', borderRadius: 10, padding: '0.6rem 0.4rem', background: '#fffdf6', position: 'sticky', top: 10 }}>
            <h4 style={{ margin: '0 0 0.4rem 0.4rem', fontSize: '0.78rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ad-ink)' }}>
                📂 {t('Distribucijske liste', 'Distribution lists')}
            </h4>
            <button
                type="button"
                onClick={() => onSelect('all')}
                style={{
                    display: 'flex', justifyContent: 'space-between', width: '100%', padding: '4px 8px', borderRadius: 6,
                    background: selected === 'all' ? 'linear-gradient(135deg, #fef3c7, rgba(254,243,199,0.5))' : 'transparent',
                    borderLeft: selected === 'all' ? '3px solid var(--ad-acc)' : '3px solid transparent',
                    border: 'none', cursor: 'pointer', fontSize: '0.86rem', color: 'var(--ad-ink)', fontWeight: selected === 'all' ? 700 : 500,
                }}
            >
                <span>📂 {t('Sve okružnice', 'All circulars')}</span>
                <span style={{ fontSize: '0.72rem', color: 'rgba(0,0,0,0.5)' }}>{allCount}</span>
            </button>
            <button
                type="button"
                onClick={() => onSelect('ungrouped')}
                style={{
                    display: 'flex', justifyContent: 'space-between', width: '100%', padding: '4px 8px', borderRadius: 6, marginBottom: 4,
                    background: selected === 'ungrouped' ? 'linear-gradient(135deg, #fef3c7, rgba(254,243,199,0.5))' : 'transparent',
                    borderLeft: selected === 'ungrouped' ? '3px solid var(--ad-acc)' : '3px solid transparent',
                    border: 'none', cursor: 'pointer', fontSize: '0.86rem', color: 'var(--ad-ink)', fontWeight: selected === 'ungrouped' ? 700 : 500,
                }}
            >
                <span>📥 {t('Bez liste', 'No list')}</span>
                <span style={{ fontSize: '0.72rem', color: 'rgba(0,0,0,0.5)' }}>{ungroupedCount}</span>
            </button>
            {roots.length === 0 ? (
                <p className="muted" style={{ margin: '0.6rem 0.4rem', fontSize: '0.78rem' }}>{t('Nema lista. Kreiraj kroz tab "Grupe".', 'No lists. Create via "Groups" tab.')}</p>
            ) : roots.map((r) => renderNode(r, 0))}
        </aside>
    );
}

function GroupsAdmin({ token, locale, groups, onReload }: { token: string; locale: Locale; groups: Group[]; onReload: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [showCreate, setShowCreate] = useState(false);
    const [createName, setCreateName] = useState('');
    const [createEmail, setCreateEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [openedGroupId, setOpenedGroupId] = useState<number | null>(null);
    const [search, setSearch] = useState('');

    const create = async () => {
        if (!createName.trim()) return;
        setBusy(true);
        try {
            await mutate('/circular-groups', token, 'POST', { name: createName.trim() });
            // Ako je upisan email, odmah ga postavi (zaseban PATCH jer store ne prima distribution_email)
            setCreateName(''); setCreateEmail(''); setShowCreate(false);
            onReload();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const del = async (id: number) => {
        if (!confirm(t('Obrisati listu?', 'Delete list?'))) return;
        try {
            await mutate(`/circular-groups/${id}`, token, 'DELETE');
            if (openedGroupId === id) setOpenedGroupId(null);
            onReload();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
    };

    const setEmail = async (id: number, email: string) => {
        try {
            await mutate(`/circular-groups/${id}`, token, 'PATCH', { distribution_email: email.trim() || null });
            onReload();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
    };

    const sorted = [...groups].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', locale === 'en' ? 'en' : 'hr'));
    const filtered = sorted.filter((g) => !search.trim() || `${g.name} ${(g as any).distribution_email ?? ''}`.toLowerCase().includes(search.toLowerCase()));
    const openedGroup = openedGroupId != null ? groups.find((g) => g.id === openedGroupId) ?? null : null;

    return (
        <div>
            {/* Compact toolbar */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('Pretraga lista…', 'Search lists…')} style={{ minWidth: 220 }} />
                <span className="muted" style={{ fontSize: '0.78rem' }}>{filtered.length} {t('lista', 'lists')}</span>
                <button type="button" onClick={() => setShowCreate(!showCreate)} className="secondary compact" style={{ marginLeft: 'auto', fontSize: '0.78rem' }}>
                    {showCreate ? '✕ ' : '+ '}{t('Nova lista', 'New list')}
                </button>
            </div>
            {/* Compact inline create row */}
            {showCreate ? (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10, padding: 8, background: 'rgba(31,122,54,0.05)', border: '1px solid rgba(31,122,54,0.25)', borderRadius: 6, flexWrap: 'wrap' }}>
                    <input value={createName} onChange={(e) => setCreateName(e.target.value)} placeholder={t('Naziv liste', 'List name')} style={{ minWidth: 200 }} autoFocus />
                    <button type="button" onClick={() => void create()} disabled={busy || !createName.trim()} style={{ background: '#1f7a36', color: '#fff', border: 0, padding: '5px 14px', borderRadius: 4, fontWeight: 700, cursor: 'pointer' }}>{busy ? '⏳' : '✓ ' + t('Kreiraj', 'Create')}</button>
                </div>
            ) : null}

            {/* Flat clean table */}
            <div className="table-wrap" style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 6 }}>
                <table className="admin-user-table" style={{ fontSize: '0.82rem', width: '100%' }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#fff' }}>
                        <tr>
                            <th>{t('Naziv liste', 'List name')}</th>
                            <th>{t('Distribucijski email', 'Distribution email')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Članova', 'Members')}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((g) => (
                            <tr key={g.id} style={{ background: openedGroupId === g.id ? 'rgba(var(--ad-acc-rgb),0.08)' : undefined }}>
                                <td style={{ fontWeight: 600, cursor: 'pointer' }} onClick={() => setOpenedGroupId(g.id)}>
                                    {g.name}
                                </td>
                                <td onClick={(e) => e.stopPropagation()}>
                                    <EmailCell value={(g as any).distribution_email ?? ''} onSave={(v) => void setEmail(g.id, v)} placeholder="lista@aurodomus.org" />
                                </td>
                                <td style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => setOpenedGroupId(g.id)}>{g.direct_member_count}</td>
                                <td onClick={(e) => e.stopPropagation()}><button type="button" onClick={() => del(g.id)} className="secondary compact" style={{ fontSize: '0.7rem', color: '#b14040' }}>🗑</button></td>
                            </tr>
                        ))}
                        {filtered.length === 0 ? <tr><td colSpan={4} className="muted" style={{ textAlign: 'center', padding: 16 }}>{t('Nema lista. Kreiraj novu ili uvezi iz mailing CSV-a.', 'No lists. Create new or import from mailing CSV.')}</td></tr> : null}
                    </tbody>
                </table>
            </div>

            {openedGroupId && openedGroup ? (
                <div role="dialog" aria-modal="true" onClick={() => setOpenedGroupId(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
                    <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(1020px, 96vw)', height: '100vh', overflowY: 'auto', background: 'linear-gradient(180deg, #fffdf6 0%, var(--ad-bg) 100%)' }}>
                        <div style={{ background: 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))', padding: '14px 20px', color: '#fffbeb', position: 'sticky', top: 0, zIndex: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                            <div style={{ minWidth: 0 }}>
                                <p style={{ margin: 0, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.14em', opacity: 0.75 }}>{t('Lista / grupa', 'List / group')}</p>
                                <h2 style={{ margin: '0.2rem 0 0', fontSize: '1.15rem', fontWeight: 700 }}>{openedGroup.name}</h2>
                                <p style={{ margin: '6px 0 0', fontSize: '0.78rem', opacity: 0.85 }}>
                                    {(openedGroup as any).distribution_email ? `✉ ${(openedGroup as any).distribution_email}` : t('Bez distribucijskog emaila', 'No distribution email')}
                                    {' · '}{openedGroup.direct_member_count} {t('članova', 'members')}
                                </p>
                            </div>
                            <button type="button" onClick={() => setOpenedGroupId(null)} style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: 0, padding: '4px 12px', borderRadius: 6, cursor: 'pointer', fontSize: '1.05rem', flexShrink: 0 }}>✕</button>
                        </div>
                        <div style={{ padding: '14px 20px' }}>
                            <GroupMembersPanel groupId={openedGroupId} token={token} locale={locale} onChanged={onReload} />
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

/** Inline editable email cell — klik za uredi, blur/Enter spremi. */
function EmailCell({ value, onSave, placeholder }: { value: string; onSave: (v: string) => void; placeholder?: string }) {
    const [edit, setEdit] = useState(false);
    const [draft, setDraft] = useState(value);
    useEffect(() => { setDraft(value); }, [value]);
    if (!edit) {
        return value
            ? <span onClick={() => setEdit(true)} style={{ cursor: 'pointer', background: 'rgba(30,64,175,0.08)', color: '#1e40af', padding: '1px 6px', borderRadius: 8, fontSize: '0.74rem' }} title="Klik za uredi">✉ {value}</span>
            : <button type="button" onClick={() => setEdit(true)} className="secondary compact" style={{ fontSize: '0.7rem' }}>+ email</button>;
    }
    return (
        <input
            type="email" value={draft} placeholder={placeholder} autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => { setEdit(false); if (draft !== value) onSave(draft); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { setEdit(false); if (draft !== value) onSave(draft); } }}
            style={{ fontSize: '0.76rem', width: 180 }}
        />
    );
}

function GroupMembersPanel({ groupId, token, locale, onChanged }: { groupId: number; token: string; locale: Locale; onChanged: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    type GroupMemberRow = {
        member_id: number;
        kind: 'bis_user' | 'zaposlenik' | 'external_email';
        name: string | null;
        email: string;
        linked_user_id?: number | null;
        sektor?: string | null;
        odjel?: string | null;
    };
    type GroupDetail = {
        group?: { distribution_email?: string | null };
        all_members?: GroupMemberRow[];
        approver_candidates?: Array<{ full_name: string }>;
    };

    const [detail, setDetail] = useState<GroupDetail | null>(null);
    const [allUsers, setAllUsers] = useState<BiAdminUser[]>([]);
    const [picker, setPicker] = useState('');
    const [memberFilter, setMemberFilter] = useState('');
    const [extEmail, setExtEmail] = useState('');
    const [extName, setExtName] = useState('');
    const [addingExternal, setAddingExternal] = useState(false);

    const load = useCallback(async () => {
        try {
            const [d, u] = await Promise.all([
                api<GroupDetail>(`/circular-groups/${groupId}`, token),
                api<{ users: BiAdminUser[] }>('/admin/users?scope=active', token),
            ]);
            setDetail(d);
            setAllUsers(u.users ?? []);
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
    }, [groupId, token]);

    useEffect(() => { void load(); }, [load]);

    const addMember = async (userId: number) => {
        try {
            await mutate(`/circular-groups/${groupId}/members`, token, 'POST', { user_id: userId });
            await load();
            onChanged();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
    };

    const addExternal = async () => {
        const email = extEmail.trim();
        if (!email) {
            alert(t('Unesi email.', 'Enter email.'));
            return;
        }
        setAddingExternal(true);
        try {
            await mutate(`/circular-groups/${groupId}/members`, token, 'POST', {
                member_email: email,
                member_name: extName.trim() || null,
            });
            setExtEmail('');
            setExtName('');
            await load();
            onChanged();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setAddingExternal(false); }
    };

    const removeMemberRow = async (memberRowId: number) => {
        try {
            await mutate(`/circular-groups/${groupId}/members/row/${memberRowId}`, token, 'DELETE');
            await load();
            onChanged();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
    };

    const kindLabel = (kind: GroupMemberRow['kind']) => {
        if (kind === 'bis_user') return t('BIS', 'BIS');
        if (kind === 'zaposlenik') return t('zaposlenik', 'employee');
        return t('vanjski', 'external');
    };

    const kindStyle = (kind: GroupMemberRow['kind']) => {
        if (kind === 'bis_user') return { bg: 'rgba(31,122,54,0.12)', color: '#1f7a36' };
        if (kind === 'zaposlenik') return { bg: 'rgba(30,64,175,0.1)', color: '#1e40af' };
        return { bg: 'rgba(var(--ad-soft-rgb),0.15)', color: 'var(--ad-ink)' };
    };

    if (!detail) return <p className="muted" style={{ fontSize: '0.78rem' }}>{t('Učitavam članove…', 'Loading members…')}</p>;

    const members = detail.all_members ?? [];
    // NULL-SAFE: dosta zaposlenika/usera nema email (npr. 106 BIS usera bez maila) → null.toLowerCase() je rušio panel (blank page). fix 2026-06-26.
    const memberEmails = new Set(members.map((m) => (m.email ?? '').toLowerCase()).filter(Boolean));
    const memberUserIds = new Set(members.filter((m) => m.kind === 'bis_user').map((m) => m.linked_user_id).filter(Boolean) as number[]);
    const filteredMembers = members.filter((m) => memberFilter === '' || matchesQuickSearch([m.name ?? '', m.email ?? ''], memberFilter));
    const candidates = allUsers.filter((u) => !!u.email && !memberUserIds.has(u.id) && !memberEmails.has((u.email ?? '').toLowerCase()) && (picker === '' || matchesQuickSearch([u.name ?? '', u.email ?? ''], picker)));

    return (
        <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.25)', borderRadius: 6, padding: 10, fontSize: '0.82rem', width: '100%', maxWidth: '100%', boxSizing: 'border-box', color: '#2f3337' }}>
            <div style={{ display: 'flex', gap: 12, fontSize: '0.74rem', color: 'var(--ad-ink)', marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span><strong>{members.length}</strong> {t('članova', 'members')}</span>
                {detail.group?.distribution_email ? <span>✉ <strong>{detail.group.distribution_email}</strong></span> : null}
            </div>
            {(detail.approver_candidates ?? []).length > 0 ? (
                <div style={{ fontSize: '0.74rem', color: 'var(--ad-acc-3)', marginBottom: 8 }}>
                    👤 {t('Odobravatelji', 'Approvers')}: {(detail.approver_candidates ?? []).map((a) => a.full_name).join(', ')}
                </div>
            ) : null}

            <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 700, color: 'var(--ad-ink)' }}>{t('Popis', 'List')}</div>
                    <input type="search" value={memberFilter} onChange={(e) => setMemberFilter(e.target.value)} placeholder={t('Filtriraj članove…', 'Filter members…')} style={{ width: 'min(220px, 100%)', padding: '3px 6px', borderRadius: 4, border: '1px solid rgba(var(--ad-soft-rgb),0.3)', fontSize: '0.76rem', boxSizing: 'border-box' }} />
                </div>
                <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid rgba(var(--ad-soft-rgb),0.2)', borderRadius: 4, background: '#fff' }}>
                    {filteredMembers.length === 0 ? (
                        <p className="muted" style={{ fontStyle: 'italic', padding: '8px 10px', margin: 0 }}>{t('Nema članova.', 'No members.')}</p>
                    ) : filteredMembers.map((m) => {
                        const st = kindStyle(m.kind);
                        return (
                            <div key={m.member_id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 8px', borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.12)' }}>
                                <div style={{ flex: '1 1 auto', minWidth: 0, color: '#2f3337' }}>
                                    <strong style={{ color: '#2f3337' }}>{m.name || m.email}</strong>
                                    {m.name ? <span className="muted" style={{ fontSize: '0.72rem' }}> · {m.email}</span> : null}
                                    <span style={{ marginLeft: 6, fontSize: '0.68rem', fontWeight: 700, background: st.bg, color: st.color, padding: '1px 6px', borderRadius: 8 }}>{kindLabel(m.kind)}</span>
                                    {m.linked_user_id && m.kind !== 'bis_user' ? <span className="muted" style={{ fontSize: '0.68rem', marginLeft: 4 }}>· BIS ✓</span> : null}
                                </div>
                                <button type="button" onClick={() => void removeMemberRow(m.member_id)} style={{ background: 'transparent', border: 0, color: '#b14040', cursor: 'pointer', fontSize: '0.78rem', flexShrink: 0, padding: '2px 4px' }} title={t('Ukloni', 'Remove')}>✕</button>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, width: '100%', minWidth: 0 }}>
                <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, marginBottom: 4, color: 'var(--ad-ink)' }}>{t('Dodaj BIS korisnika', 'Add BIS user')}</div>
                    <input type="search" value={picker} onChange={(e) => setPicker(e.target.value)} placeholder={t('Filtriraj…', 'Filter…')} style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', padding: '3px 6px', borderRadius: 4, border: '1px solid rgba(var(--ad-soft-rgb),0.3)', marginBottom: 4 }} />
                    <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid rgba(var(--ad-soft-rgb),0.15)', borderRadius: 4 }}>
                        {candidates.slice(0, 30).map((u) => (
                            <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '4px 6px', cursor: 'pointer', color: '#2f3337' }} onClick={() => void addMember(u.id)}>
                                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name} <span className="muted" style={{ fontSize: '0.72rem' }}>· {u.email}</span></span>
                                <span style={{ color: '#1f7a36', flexShrink: 0 }}>＋</span>
                            </div>
                        ))}
                        {candidates.length === 0 ? <span className="muted" style={{ fontSize: '0.74rem', fontStyle: 'italic', padding: '6px 8px', display: 'block' }}>—</span> : null}
                    </div>
                </div>
                <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, marginBottom: 4, color: 'var(--ad-ink)' }}>{t('Dodaj vanjski email', 'Add external email')}</div>
                    <p className="muted" style={{ fontSize: '0.72rem', margin: '0 0 6px' }}>{t('Bez BIS računa — okružnica stiže na email.', 'No BIS account — circular goes to email.')}</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <input type="email" value={extEmail} onChange={(e) => setExtEmail(e.target.value)} placeholder="ime.prezime@firma.hr" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', padding: '4px 6px', borderRadius: 4, border: '1px solid rgba(var(--ad-soft-rgb),0.3)' }} />
                        <input type="text" value={extName} onChange={(e) => setExtName(e.target.value)} placeholder={t('Ime (opcionalno)', 'Name (optional)')} style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', padding: '4px 6px', borderRadius: 4, border: '1px solid rgba(var(--ad-soft-rgb),0.3)' }} />
                        <button type="button" disabled={addingExternal || !extEmail.trim()} onClick={() => void addExternal()} style={{ alignSelf: 'flex-start', background: 'var(--ad-ink)', color: '#fff', border: 0, padding: '5px 12px', borderRadius: 4, fontWeight: 700, cursor: 'pointer', fontSize: '0.78rem', marginTop: 2 }}>
                            ＋ {t('Dodaj email', 'Add email')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Poslovnice/role-mailbox (npr. kutina@aurodomus.org, uprava@…) NISU osobe → ne nude se u "Pojedinci".
// Heuristika: @aurodomus.* a lokalni dio bez točke (osobe su ime.prezime@). Dostupne su preko grupa "Poslovnice".
function isBranchOrRoleEmail(email: string): boolean {
    const m = /^([^@]+)@aurodomus\.(org|eu)$/i.exec((email || '').trim());
    return !!m && !m[1].includes('.');
}

function CircularCompose({ token, locale, groups, replyTo, draftId, onSent }: { token: string; locale: Locale; groups: Group[]; replyTo: { parentId: number; subject: string } | null; draftId?: number | null; onSent: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [subject, setSubject] = useState(replyTo ? `Re: ${replyTo.subject}` : '');
    const [htmlBody, setHtmlBody] = useState('');
    const [users, setUsers] = useState<BiAdminUser[]>([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [pickedUserIds, setPickedUserIds] = useState<Set<number>>(new Set());
    const [pickedGroupIds, setPickedGroupIds] = useState<Set<number>>(new Set());
    const [includeSubgroups, setIncludeSubgroups] = useState(true);
    const [recipientsOpen, setRecipientsOpen] = useState(false);
    const [groupQuery, setGroupQuery] = useState('');
    const [allowDownload, setAllowDownload] = useState(false);
    const [query, setQuery] = useState('');
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState<string | null>(null);
    const [resultErr, setResultErr] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [resolvedRecipients, setResolvedRecipients] = useState<ResolvedRecipientPreview[]>([]);
    const [resolvedLoading, setResolvedLoading] = useState(false);
    const [extraApproverIds, setExtraApproverIds] = useState<number[]>([]);
    const [zaposlenici, setZaposlenici] = useState<Array<{ id: number; full_name: string; email: string }>>([]);
    const [goesToMp, setGoesToMp] = useState(false);
    const [boardId, setBoardId] = useState<number | null>(null);
    const [boardMembers, setBoardMembers] = useState<Array<{ id: number; full_name: string; email: string }>>([]);
    const [attachments, setAttachments] = useState<File[]>([]); // više PDF priloga (i dalje samo PDF)
    // Naziv već priloženog PDF-a kad uređuješ postojeću okružnicu (File input se ne može repopulirati) —
    // ostaje osim ako se dodaju novi. (fix 2026-06-17: "uredi" je gubio PDF)
    const [existingAttachmentName, setExistingAttachmentName] = useState<string | null>(null);
    // m.8 v2 — fiksna forma (default) vs napredni HTML editor
    const [composeMode, setComposeMode] = useState<'form' | 'html'>('form');
    const [fSektor, setFSektor] = useState('');
    const [fOdjel, setFOdjel] = useState('');
    const [fPrioritet, setFPrioritet] = useState<'NORMALNO' | 'VAŽNO' | 'KRITIČNO'>('NORMALNO');
    const [fVrsta, setFVrsta] = useState<'OBAVIJEST' | 'UPUTA' | 'SOP'>('OBAVIJEST');
    const [fDatumIzdavanja, setFDatumIzdavanja] = useState<string>(() => new Date().toISOString().slice(0, 10));
    const [fVrijediOd, setFVrijediOd] = useState('');
    const [fVrijediDo, setFVrijediDo] = useState('');
    const [fVrijediTrajno, setFVrijediTrajno] = useState(false);
    const [fRazlog, setFRazlog] = useState('');
    const [fMijenja, setFMijenja] = useState('');
    const [fKoga, setFKoga] = useState('');
    const [fRokovi, setFRokovi] = useState('');

    // Uređivanje SKICE: učitaj postojeću okružnicu i popuni cijeli obrazac (uklj. primatelje).
    useEffect(() => {
        if (!draftId) return;
        api<{ circular: Record<string, unknown>; recipient_user_ids?: number[] }>(`/circulars/${draftId}`, token)
            .then((d) => {
                const c = d.circular as any;
                const iso = (v: unknown) => (v ? String(v).slice(0, 10) : '');
                setSubject(c.subject ?? '');
                setHtmlBody(c.html_body ?? '');
                setComposeMode(c.compose_mode === 'html' ? 'html' : 'form');
                setFSektor(c.sektor ?? '');
                setFOdjel(c.odjel ?? '');
                setFPrioritet(c.prioritet === 'VAŽNO' ? 'VAŽNO' : 'NORMALNO');
                setFVrsta(['OBAVIJEST', 'UPUTA', 'SOP'].includes(c.vrsta) ? c.vrsta : 'OBAVIJEST');
                if (c.datum_izdavanja) setFDatumIzdavanja(iso(c.datum_izdavanja));
                setFVrijediOd(iso(c.vrijedi_od));
                setFVrijediDo(iso(c.vrijedi_do));
                setFVrijediTrajno(!!c.vrijedi_trajno);
                setAllowDownload(!!c.allow_download);
                let fd: any = c.form_data;
                if (typeof fd === 'string') { try { fd = JSON.parse(fd); } catch { fd = null; } }
                if (fd && typeof fd === 'object') {
                    setFRazlog(fd.razlog ?? ''); setFMijenja(fd.mijenja ?? ''); setFKoga(fd.koga ?? ''); setFRokovi(fd.rokovi ?? '');
                }
                if (Array.isArray(d.recipient_user_ids)) setPickedUserIds(new Set(d.recipient_user_ids));
                let gids: any = c.group_ids;
                if (typeof gids === 'string') { try { gids = JSON.parse(gids); } catch { gids = []; } }
                if (Array.isArray(gids)) setPickedGroupIds(new Set(gids.map((x: any) => Number(x))));
                if (c.include_subgroups != null) setIncludeSubgroups(!!c.include_subgroups);
                // Repopuliraj lanac odobrenja + prilog (inače "uredi" izgubi člana uprave i PDF). fix 2026-06-17.
                if (c.board_zaposlenik_id) setBoardId(Number(c.board_zaposlenik_id));
                setGoesToMp(!!c.goes_to_mp);
                // Repopuliraj eksplicitno odabrane odobravatelje (picker) — inače "uredi" izgubi dodanog. fix 2026-06-18.
                let dApr: any = c.draft_approver_ids;
                if (typeof dApr === 'string') { try { dApr = JSON.parse(dApr); } catch { dApr = null; } }
                if (Array.isArray(dApr)) setExtraApproverIds(dApr.map((x: any) => Number(x)).filter((n: number) => Number.isFinite(n)));
                setExistingAttachmentName(c.attachment_name ? String(c.attachment_name) : null);
                setRecipientsOpen(true);
            })
            .catch((e) => { setResult(e instanceof Error ? e.message : 'API'); setResultErr(true); });
    }, [draftId, token]);

    useEffect(() => {
        if (pickedGroupIds.size === 0 && pickedUserIds.size === 0) {
            setResolvedRecipients([]);
            return;
        }
        let cancelled = false;
        setResolvedLoading(true);
        api<{ rows: ResolvedRecipientPreview[] }>('/circular-groups/preview-recipients', token, {
            method: 'POST',
            body: JSON.stringify({
                recipient_user_ids: Array.from(pickedUserIds),
                recipient_group_ids: Array.from(pickedGroupIds),
                include_subgroups: includeSubgroups,
            }),
        })
            .then((d) => { if (!cancelled) setResolvedRecipients(d.rows ?? []); })
            .catch(() => { if (!cancelled) setResolvedRecipients([]); })
            .finally(() => { if (!cancelled) setResolvedLoading(false); });
        return () => { cancelled = true; };
    }, [pickedGroupIds, pickedUserIds, includeSubgroups, token]);

    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const para = (s: string) => esc(s).trim().split(/\n{2,}/).map((p) => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
    const buildHtmlFromForm = (): string => {
        const meta: string[] = [];
        if (fSektor || fOdjel) meta.push(`<strong>Pošiljatelj:</strong> ${esc([fSektor, fOdjel].filter(Boolean).join(' / '))}`);
        meta.push(`<strong>Vrsta:</strong> ${esc(fVrsta)}`);
        if (fPrioritet !== 'NORMALNO') meta.push(`<strong>Prioritet:</strong> ${fPrioritet}`);
        if (fDatumIzdavanja) meta.push(`<strong>Datum izdavanja:</strong> ${formatDate(fDatumIzdavanja, 'hr')}`);
        const vrijedi = fVrijediTrajno ? 'Trajno' : [fVrijediOd ? `od ${formatDate(fVrijediOd, 'hr')}` : '', fVrijediDo ? `do ${formatDate(fVrijediDo, 'hr')}` : ''].filter(Boolean).join(' ');
        if (vrijedi) meta.push(`<strong>Vrijedi:</strong> ${esc(vrijedi)}`);
        const sec = (n: string, body: string) => body.trim() ? `<h3>${n}</h3>${para(body)}` : '';
        return [
            meta.length ? `<p style="font-size:0.9em;color:#555;border-bottom:1px solid #e7dcc0;padding-bottom:8px">${meta.join(' &nbsp;·&nbsp; ')}</p>` : '',
            sec('1. RAZLOG / KONTEKST', fRazlog),
            sec('2. ŠTO SE MIJENJA / UVODI', fMijenja),
            sec('3. KOGA SE TIČE I ŠTO TKO TREBA NAPRAVITI', fKoga),
            sec('4. ROKOVI I PRIJELAZNO RAZDOBLJE', fRokovi),
        ].filter(Boolean).join('\n');
    };
    const appendFormFields = (fd: FormData) => {
        fd.append('compose_mode', composeMode);
        // META (uklj. Pošiljatelj — sektor/odjel) šalje se u OBA moda (Forma i Napredni HTML). fix 2026-07-10.
        if (fSektor) fd.append('sektor', fSektor);
        if (fOdjel) fd.append('odjel', fOdjel);
        if (fPrioritet !== 'NORMALNO') fd.append('prioritet', fPrioritet);
        fd.append('vrsta', fVrsta);
        if (fDatumIzdavanja) fd.append('datum_izdavanja', fDatumIzdavanja);
        if (fVrijediOd) fd.append('vrijedi_od', fVrijediOd);
        if (fVrijediDo) fd.append('vrijedi_do', fVrijediDo);
        fd.append('vrijedi_trajno', fVrijediTrajno ? '1' : '0');
        // form_data (strukturirane sekcije tijela) samo u 'form' modu.
        if (composeMode !== 'form') return;
        // form_data MORA stići kao polje (backend pravilo: 'array') — šalji po ključu, ne kao JSON string
        // (inače Laravel vidi string → "The form data field must be an array").
        const formDataObj: Record<string, string> = { naslov: subject, razlog: fRazlog, mijenja: fMijenja, koga: fKoga, rokovi: fRokovi };
        Object.entries(formDataObj).forEach(([k, v]) => fd.append(`form_data[${k}]`, v ?? ''));
    };
    const resetForm = () => {
        setSubject(''); setHtmlBody(''); setPickedUserIds(new Set()); setPickedGroupIds(new Set());
        setExtraApproverIds([]); setAttachments([]); setExistingAttachmentName(null); setAllowDownload(false);
        setGoesToMp(false); setBoardId(null);
        setFSektor(''); setFOdjel(''); setFPrioritet('NORMALNO'); setFVrsta('OBAVIJEST');
        setFVrijediOd(''); setFVrijediDo(''); setFVrijediTrajno(false);
        setFRazlog(''); setFMijenja(''); setFKoga(''); setFRokovi('');
    };

    useEffect(() => {
        setUsersLoading(true);
        api<{ users: BiAdminUser[] }>('/admin/users?scope=active', token)
            .then((r) => setUsers(r.users.filter((u) => u.email && u.email.trim() !== '' && !isBranchOrRoleEmail(u.email))))
            .finally(() => setUsersLoading(false));
        // Picker odobravatelja: auth-only endpoint (ne superadmin-only /admin/zaposlenici) da ga vide SVI
        // kreatori okružnica, ne samo superadmini (npr. Mira). Filter = članovi uprave (showInPicker).
        void api<{ rows: Array<{ id: number; full_name: string; email: string }> }>('/circulars/approver-candidates', token)
            .then((r) => setZaposlenici(r.rows)).catch(() => {});
        void api<{ board: Array<{ id: number; full_name: string; email: string }> }>('/circulars/board-members', token)
            .then((r) => setBoardMembers(r.board)).catch(() => {});
    }, [token]);

    const filteredUsers = useMemo(() => users.filter((u) => matchesQuickSearch([u.name, u.email, u.primary_department_label, u.primary_department_region, u.primary_department_city, u.company_label], query)), [users, query]);
    const toggleUser = (id: number) => setPickedUserIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const toggleGroup = (id: number) => setPickedGroupIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const send = async () => {
        const composedBody = composeMode === 'form' ? buildHtmlFromForm() : htmlBody;
        if (!subject.trim() || !composedBody.trim()) { alert(t('Naslov i sadržaj su obavezni.', 'Subject and body required.')); return; }
        if (pickedUserIds.size === 0 && pickedGroupIds.size === 0) { alert(t('Odaberi barem 1 grupu ili korisnika.', 'Pick at least 1 group or user.')); return; }
        // Slanje samo pojedincima (bez grupe) → mora se izabrati odobravatelj (grupe daju nadležnog automatski).
        if (pickedGroupIds.size === 0 && extraApproverIds.length === 0) {
            alert(t('Slanje samo pojedincima zahtijeva odobrenje — odaberi barem jednog odobravatelja.', 'Sending to individuals requires an approver.')); return;
        }
        // Uprava je OBAVEZNA — barem jedan odabrani odobravatelj mora biti član uprave (zadnji potpis).
        const boardIdSet = new Set(boardMembers.map((b) => b.id));
        if (!extraApproverIds.some((id) => boardIdSet.has(id))) {
            alert(t('Odaberi barem jednog člana uprave (označen „uprava") — on je zadnji potpis u lancu odobrenja.', 'Select at least one board member (marked "uprava") — the final sign-off.')); return;
        }
        if (!confirm(t('Poslati okružnicu NA ODOBRENJE? Šalje se primateljima tek kad CIJELI lanac odobri (uprava je zadnja).', 'Submit circular for approval? Sent only after the whole chain approves (board last).'))) return;
        setSending(true); setResult(null); setResultErr(false);
        try {
            const fd = new FormData();
            fd.append('subject', subject);
            fd.append('html_body', composedBody);
            appendFormFields(fd);
            Array.from(pickedUserIds).forEach((id) => fd.append('recipient_user_ids[]', String(id)));
            Array.from(pickedGroupIds).forEach((id) => fd.append('recipient_group_ids[]', String(id)));
            fd.append('include_subgroups', includeSubgroups ? '1' : '0');
            if (replyTo?.parentId) fd.append('parent_circular_id', String(replyTo.parentId));
            extraApproverIds.forEach((id) => fd.append('approver_zaposlenik_ids[]', String(id)));
            if (boardId) fd.append('board_zaposlenik_id', String(boardId));
            fd.append('goes_to_mp', goesToMp ? '1' : '0');
            if (attachments.length) { attachments.forEach((f) => fd.append('attachments[]', f)); fd.append('allow_download', allowDownload ? '1' : '0'); }
            else if (draftId && existingAttachmentName) { fd.append('keep_attachment_circular_id', String(draftId)); fd.append('allow_download', allowDownload ? '1' : '0'); }
            const r = await uploadFile<{ ok: boolean; circular_id: number; mode?: string; approver_count?: number; approval_sent?: number; error?: string }>('/circulars', token, fd);
            setResult(t(`Poslano na odobrenje — ${r.approver_count ?? 0} odobravatelja (${r.approval_sent ?? 0} mailova). Šalje se kad SVI odobre.`, `Submitted for approval — ${r.approver_count ?? 0} approvers.`));
            resetForm();
            setTimeout(() => onSent(), 1800);
        } catch (e) { setResult(e instanceof Error ? e.message : 'API'); setResultErr(true); }
        finally { setSending(false); }
    };

    // Spremi kao SKICU — bez slanja/odobravanja. Samo naslov je obavezan; primatelje/sadržaj
    // možeš dovršiti kasnije (otvori skicu → uredi → "Pošalji na odobrenje").

    /** Nakon izmjene sadržaja staro odobrenje ne vrijedi — ponudi povratak u krug odmah. */
    const maybeResubmit = async (res: { needs_resubmit?: boolean } | undefined, id: number) => {
        if (!res?.needs_resubmit) return;
        if (!confirm(t('Sadržaj je izmijenjen pa prethodno odobrenje više ne vrijedi.\n\nPoslati odmah na (ponovno) odobrenje?', 'Content changed so the previous approval no longer applies.\n\nSubmit for (re)approval now?'))) return;
        try {
            await mutate(`/circulars/${id}/submit`, token, 'POST', {});
            setResult(t('📋 Poslano na ponovno odobrenje.', '📋 Submitted for re-approval.'));
        } catch (e) {
            setResult(e instanceof Error ? e.message : 'API'); setResultErr(true);
        }
    };

    const saveDraft = async () => {
        if (!subject.trim()) { alert(t('Naslov je obavezan za spremanje skice.', 'Subject required to save a draft.')); return; }
        setSending(true); setResult(null); setResultErr(false);
        const composedBody = composeMode === 'form' ? buildHtmlFromForm() : htmlBody;
        try {
            // Uredivanje postojeće skice → PATCH (uklj. re-sync primatelja); inače kreiraj novu (POST).
            // Ako je odabran NOVI PDF prilog → multipart (POST+_method=PATCH), jer JSON ne nosi File. fix 2026-06-26.
            if (draftId && attachments.length) {
                const fd = new FormData();
                fd.append('subject', subject);
                fd.append('html_body', composedBody);
                appendFormFields(fd);
                Array.from(pickedUserIds).forEach((id) => fd.append('recipient_user_ids[]', String(id)));
                Array.from(pickedGroupIds).forEach((id) => fd.append('recipient_group_ids[]', String(id)));
                fd.append('include_subgroups', includeSubgroups ? '1' : '0');
                fd.append('goes_to_mp', goesToMp ? '1' : '0');
                if (boardId) fd.append('board_zaposlenik_id', String(boardId));
                extraApproverIds.forEach((id) => fd.append('approver_zaposlenik_ids[]', String(id)));
                attachments.forEach((f) => fd.append('attachments[]', f));
                fd.append('allow_download', allowDownload ? '1' : '0');
                const rUp = await uploadFileMethod<{ needs_resubmit?: boolean }>(`/circulars/${draftId}`, token, fd, 'PATCH');
                setResult(t('💾 Izmjene skice + PDF prilozi spremljeni.', '💾 Draft changes + PDFs saved.'));
                await maybeResubmit(rUp, draftId);
                setTimeout(() => onSent(), 1200);
                return;
            }
            if (draftId) {
                const rJson = await mutate<{ needs_resubmit?: boolean }>(`/circulars/${draftId}`, token, 'PATCH', {
                    subject,
                    html_body: composedBody,
                    compose_mode: composeMode,
                    form_data: { naslov: subject, razlog: fRazlog, mijenja: fMijenja, koga: fKoga, rokovi: fRokovi },
                    sektor: fSektor || null,
                    odjel: fOdjel || null,
                    prioritet: fPrioritet !== 'NORMALNO' ? fPrioritet : null,
                    vrsta: fVrsta,
                    datum_izdavanja: fDatumIzdavanja || null,
                    vrijedi_od: fVrijediOd || null,
                    vrijedi_do: fVrijediDo || null,
                    vrijedi_trajno: fVrijediTrajno,
                    allow_download: allowDownload,
                    recipient_user_ids: Array.from(pickedUserIds),
                    recipient_group_ids: Array.from(pickedGroupIds),
                    include_subgroups: includeSubgroups,
                    goes_to_mp: goesToMp,
                    board_zaposlenik_id: boardId,
                    approver_zaposlenik_ids: extraApproverIds,
                });
                setResult(t('💾 Izmjene skice spremljene (primatelji + odobravatelji uključeni).', '💾 Draft changes saved (recipients + approvers included).'));
                await maybeResubmit(rJson, draftId);
                setTimeout(() => onSent(), 1200);
                return;
            }
            const fd = new FormData();
            fd.append('save_draft', '1');
            fd.append('subject', subject);
            fd.append('html_body', composedBody);
            appendFormFields(fd);
            Array.from(pickedUserIds).forEach((id) => fd.append('recipient_user_ids[]', String(id)));
            Array.from(pickedGroupIds).forEach((id) => fd.append('recipient_group_ids[]', String(id)));
            fd.append('include_subgroups', includeSubgroups ? '1' : '0');
            if (replyTo?.parentId) fd.append('parent_circular_id', String(replyTo.parentId));
            extraApproverIds.forEach((id) => fd.append('approver_zaposlenik_ids[]', String(id)));
            if (boardId) fd.append('board_zaposlenik_id', String(boardId));
            fd.append('goes_to_mp', goesToMp ? '1' : '0');
            if (attachments.length) { attachments.forEach((f) => fd.append('attachments[]', f)); fd.append('allow_download', allowDownload ? '1' : '0'); }
            await uploadFile<{ ok: boolean; circular_id: number; mode?: string }>('/circulars', token, fd);
            setResult(t('💾 Skica spremljena. Otvori je iz liste kad je želiš dovršiti i poslati na odobrenje.', '💾 Draft saved. Open it from the list to finish and submit.'));
            resetForm();
            setTimeout(() => onSent(), 1400);
        } catch (e) { setResult(e instanceof Error ? e.message : 'API'); setResultErr(true); }
        finally { setSending(false); }
    };

    const groupTree = useMemo(() => {
        const map = new Map<number | null, Group[]>();
        for (const g of groups) {
            const p = g.parent_group_id;
            const arr = map.get(p) ?? []; arr.push(g); map.set(p, arr);
        }
        for (const arr of map.values()) arr.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', locale === 'en' ? 'en' : 'hr'));
        return map;
    }, [groups, locale]);

    const renderGroupOption = (g: Group, depth: number): React.ReactNode => {
        const isPicked = pickedGroupIds.has(g.id);
        const children = groupTree.get(g.id) ?? [];
        return (
            <React.Fragment key={g.id}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px', cursor: 'pointer', background: isPicked ? 'rgba(31,122,54,0.08)' : 'transparent', borderRadius: 4, marginLeft: depth * 14, fontSize: '0.82rem' }}>
                    <input type="checkbox" checked={isPicked} onChange={() => toggleGroup(g.id)} />
                    {g.name}
                    <span className="muted" style={{ fontSize: '0.72rem' }}>{g.direct_member_count}</span>
                </label>
                {children.map((c) => renderGroupOption(c, depth + 1))}
            </React.Fragment>
        );
    };

    const recipientSummary = `${pickedGroupIds.size} ${t('grupa', 'groups')} · ${pickedUserIds.size} ${t('pojedinaca', 'individuals')}`;

    return (
        <div className="okr-doc okr-card">
            {replyTo ? (
                <div style={{ background: 'rgba(var(--ad-acc-rgb),0.10)', border: '1px solid rgba(var(--ad-acc-rgb),0.35)', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: '0.84rem' }}>
                    💬 {t('Odgovor na okružnicu', 'Reply to circular')} #{replyTo.parentId}: <strong>{replyTo.subject}</strong>
                </div>
            ) : null}

            {/* PRIMATELJI: jedan blok za OBA moda — unutar metadata kvadrata niže (duplikat za HTML mod uklonjen 2026-07-10). */}

            {/* Pošiljatelj — sektor/odjel: UVIJEK vidljivo (i u "Forma" i u "Napredni HTML" modu).
                Prije je bilo samo u 'form' modu → tko šalje u HTML modu ostao bi prazan i okružnica
                je pokazivala "Sektor: — / —" (npr. Mira Sekula). */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10, padding: 12, border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 8, background: '#fffdf6' }}>
                <label className="filter-label" style={{ margin: 0 }}>{t('Pošiljatelj — sektor', 'Sender — sector')}
                    <input value={fSektor} onChange={(e) => setFSektor(e.target.value)} placeholder={t('npr. Prodaja', 'e.g. Sales')} /></label>
                <label className="filter-label" style={{ margin: 0 }}>{t('Pošiljatelj — odjel', 'Sender — dept')}
                    <input value={fOdjel} onChange={(e) => setFOdjel(e.target.value)} placeholder={t('npr. Maloprodaja', 'e.g. Retail')} /></label>
            </div>

            {/* NAČIN UNOSA: fiksna forma (default) ili napredni HTML */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="button" onClick={() => setComposeMode('form')} className={composeMode === 'form' ? '' : 'secondary'} style={{ fontSize: '0.8rem' }}>📋 {t('Forma', 'Form')}</button>
                <button type="button" onClick={() => setComposeMode('html')} className={composeMode === 'html' ? '' : 'secondary'} style={{ fontSize: '0.8rem' }}>{'</>'} {t('Napredni HTML', 'Advanced HTML')}</button>
                <span className="muted" style={{ fontSize: '0.74rem', marginLeft: 'auto' }}>
                    {t('Broj', 'Number')}: <strong>OK-{new Date().getFullYear()}-{((fOdjel || 'OPCE').toUpperCase().replace(/[^A-Z0-9]/g, '') || 'OPCE').slice(0, 20)}-NNN</strong> <em>({t('auto', 'auto')})</em>
                </span>
            </div>

            {/* METADATA KVADRAT (Vrsta/Prioritet/Datumi/Trajno + Primatelji) — FIKSNO u OBA moda (Forma i Napredni HTML). 2026-07-10. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 12, padding: 12, border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 8, background: '#fffdf6' }}>
                    <label className="filter-label" style={{ margin: 0 }}>{t('Vrsta', 'Type')}
                        <select value={fVrsta} onChange={(e) => setFVrsta(e.target.value as typeof fVrsta)}>
                            <option value="OBAVIJEST">OBAVIJEST</option><option value="UPUTA">UPUTA</option><option value="SOP">SOP</option>
                        </select></label>
                    <label className="filter-label" style={{ margin: 0 }}>{t('Prioritet', 'Priority')}
                        <select value={fPrioritet} onChange={(e) => setFPrioritet(e.target.value as typeof fPrioritet)}>
                            <option value="NORMALNO">{t('Standard', 'Standard')}</option>
                            <option value="VAŽNO">{t('Važno', 'Important')}</option>
                            <option value="KRITIČNO">{t('Kritično', 'Critical')}</option>
                        </select></label>
                    <label className="filter-label" style={{ margin: 0 }}>{t('Datum izdavanja', 'Issued')}
                        <DateInput value={fDatumIzdavanja} onChange={setFDatumIzdavanja} /></label>
                    <label className="filter-label" style={{ margin: 0 }}>{t('Vrijedi od', 'Valid from')}
                        <DateInput value={fVrijediOd} onChange={setFVrijediOd} /></label>
                    <label className="filter-label" style={{ margin: 0 }}>{t('Vrijedi do', 'Valid to')}
                        <DateInput value={fVrijediDo} onChange={setFVrijediDo} disabled={fVrijediTrajno} /></label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', alignSelf: 'end', paddingBottom: 6 }}>
                        <input type="checkbox" checked={fVrijediTrajno} onChange={(e) => setFVrijediTrajno(e.target.checked)} /> {t('Trajno', 'Permanent')}
                    </label>
                    {/* PRIMATELJI (Iva 2026-06-03): ispod Pošiljatelja u istom kvadratu, bez accordion-a */}
                    <div style={{ gridColumn: '1 / -1', borderTop: '1px solid rgba(var(--ad-soft-rgb),0.3)', marginTop: 4, paddingTop: 10 }}>
                        <div style={{ fontWeight: 700, color: 'var(--ad-ink)', marginBottom: 6 }}>👥 {t('Primatelji', 'Recipients')} <span className="muted" style={{ fontWeight: 400, fontSize: '0.82rem' }}>— {recipientSummary}</span></div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                                    <strong style={{ fontSize: '0.84rem' }}>👥 {t('Grupe', 'Groups')}</strong>
                                    <label style={{ fontSize: '0.74rem', display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
                                        <input type="checkbox" checked={includeSubgroups} onChange={(e) => setIncludeSubgroups(e.target.checked)} />
                                        {t('Uključi podgrupe', 'Include subgroups')}
                                    </label>
                                </div>
                                <QuickSearchInput value={groupQuery} onChange={setGroupQuery} placeholder={t('Filtriraj liste…', 'Filter lists…')} />
                                <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid rgba(var(--ad-soft-rgb),0.25)', borderRadius: 6, padding: 6, marginTop: 4, background: '#fff' }}>
                                    {groups.length === 0 ? <span className="muted" style={{ fontStyle: 'italic', fontSize: '0.8rem' }}>{t('Nema grupa. Kreiraj u tabu "Grupe".', 'No groups.')}</span>
                                     : groupQuery.trim() ? (
                                        groups.filter((g) => matchesQuickSearch([g.name], groupQuery)).map((g) => (
                                            <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', cursor: 'pointer', background: pickedGroupIds.has(g.id) ? 'rgba(31,122,54,0.08)' : 'transparent', borderRadius: 4, fontSize: '0.82rem' }}>
                                                <input type="checkbox" checked={pickedGroupIds.has(g.id)} onChange={() => toggleGroup(g.id)} />
                                                {g.name}
                                                <span className="muted" style={{ fontSize: '0.72rem' }}>{g.direct_member_count}</span>
                                            </label>
                                        ))
                                     ) : (groupTree.get(null) ?? []).map((g) => renderGroupOption(g, 0))}
                                </div>
                            </div>
                            <div>
                                <strong style={{ fontSize: '0.84rem' }}>👤 {t('Pojedinci', 'Individuals')}</strong>
                                {usersLoading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : (
                                    <>
                                        <QuickSearchInput value={query} onChange={setQuery} placeholder={t('Filtriraj…', 'Filter…')} />
                                        <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid rgba(var(--ad-soft-rgb),0.25)', borderRadius: 6, padding: 6, marginTop: 4 }}>
                                            {filteredUsers.map((u) => {
                                                const isPicked = pickedUserIds.has(u.id);
                                                return (
                                                    <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', cursor: 'pointer', background: isPicked ? 'rgba(31,122,54,0.08)' : 'transparent', borderRadius: 4, fontSize: '0.82rem' }}>
                                                        <input type="checkbox" checked={isPicked} onChange={() => toggleUser(u.id)} />
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontWeight: 600 }}>{u.name}</div>
                                                            <div className="muted" style={{ fontSize: '0.72rem' }}>{u.email}</div>
                                                        </div>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        {pickedGroupIds.size > 0 || pickedUserIds.size > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                                {groups.filter((g) => pickedGroupIds.has(g.id)).map((g) => (
                                    <span key={g.id} style={{ background: 'rgba(31,122,54,0.1)', color: '#1f7a36', padding: '2px 9px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                                        👥 {g.name}
                                        <button type="button" onClick={() => toggleGroup(g.id)} style={{ border: 0, background: 'transparent', color: '#1f7a36', cursor: 'pointer', padding: 0, lineHeight: 1 }}>✕</button>
                                    </span>
                                ))}
                                {pickedUserIds.size > 0 ? <span style={{ background: 'rgba(107,79,28,0.1)', color: 'var(--ad-ink)', padding: '2px 9px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600 }}>👤 {pickedUserIds.size} {t('pojedinaca', 'individuals')}</span> : null}
                            </div>
                        ) : null}
                        {resolvedRecipients.length > 0 || resolvedLoading ? (
                            <div style={{ marginTop: 10, borderTop: '1px solid rgba(var(--ad-soft-rgb),0.25)', paddingTop: 8 }}>
                                {/* Prvo grupe (distribucijske liste) koje primaju, pa tek pojedinačni primatelji. */}
                                {!resolvedLoading ? (() => {
                                    const grpMap = new Map<number, string>();
                                    resolvedRecipients.forEach((r) => (r.source_groups ?? []).forEach((g) => { if (g && g.name) grpMap.set(g.id, g.name); }));
                                    const grps = Array.from(grpMap.values()).sort((a, b) => a.localeCompare(b));
                                    const directCount = resolvedRecipients.filter((r) => r.source_direct && (r.source_groups ?? []).length === 0).length;
                                    return grps.length > 0 || directCount > 0 ? (
                                        <div style={{ marginBottom: 8 }}>
                                            <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ad-acc-3)', marginBottom: 4 }}>
                                                {t('Grupe koje primaju', 'Receiving groups')} ({grps.length})
                                            </div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                {grps.map((name) => (
                                                    <span key={name} style={{ background: 'rgba(var(--ad-acc-rgb),0.15)', color: 'var(--ad-ink)', border: '1px solid rgba(var(--ad-soft-rgb),0.4)', borderRadius: 12, padding: '2px 10px', fontSize: '0.74rem', fontWeight: 600 }}>👥 {name}</span>
                                                ))}
                                                {directCount > 0 ? <span style={{ background: 'rgba(31,122,54,0.12)', color: '#1f7a36', border: '1px solid rgba(31,122,54,0.35)', borderRadius: 12, padding: '2px 10px', fontSize: '0.74rem', fontWeight: 600 }}>👤 {t('Pojedinačno', 'Direct')} ({directCount})</span> : null}
                                            </div>
                                        </div>
                                    ) : null;
                                })() : null}
                                <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ad-acc-3)', marginBottom: 6 }}>
                                    {t('Primatelji (svaki samo jednom)', 'Recipients (each once)')}
                                    {resolvedLoading ? ` (${t('učitavanje…', 'loading…')})` : ` (${resolvedRecipients.length})`}
                                </div>
                                {!resolvedLoading && resolvedRecipients.length > 0 ? (
                                    <div className="table-wrap" style={{ maxHeight: 220, overflowY: 'auto' }}>
                                        <table className="admin-user-table" style={{ fontSize: '0.78rem' }}>
                                            <thead>
                                                <tr>
                                                    <th>{t('Primatelj', 'Recipient')}</th>
                                                    <th>{t('Izvor', 'Source')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {resolvedRecipients.map((r) => (
                                                    <tr key={r.email}>
                                                        <td>
                                                            <div style={{ fontWeight: 600 }}>{r.name || r.email}</div>
                                                            {r.name ? <div className="muted" style={{ fontSize: '0.72rem' }}>{r.email}</div> : null}
                                                        </td>
                                                        <td style={{ color: 'var(--ad-acc-3)' }}>{recipientSourceText(r, locale) || '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                </div>

            {/* NASLOV */}
            <input className="okr-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={composeMode === 'form' ? t('NASLOV OKRUŽNICE (kratko, 5–10 riječi)', 'CIRCULAR TITLE (short, 5–10 words)') : t('Naslov okružnice…', 'Circular subject…')} />

            {/* SADRŽAJ */}
            {composeMode === 'form' ? (
                <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
                    {([
                        ['1. RAZLOG / KONTEKST', fRazlog, setFRazlog, t('Zašto se izdaje? Pozadina i svrha (2–4 rečenice).', 'Why issued? Background and purpose.')],
                        ['2. ŠTO SE MIJENJA / UVODI', fMijenja, setFMijenja, t('Što je novo, što ostaje, što se ukida.', 'What changes / stays / is removed.')],
                        ['3. KOGA SE TIČE I ŠTO TKO TREBA NAPRAVITI', fKoga, setFKoga, t('Po točkama, prema odjelu/funkciji (Voditelji, Skladište, Nabava, Financije, IT…).', 'Bullet points per dept/role.')],
                        ['4. ROKOVI I PRIJELAZNO RAZDOBLJE', fRokovi, setFRokovi, t('Datum primjene, prijelazno razdoblje, datumi kontrole.', 'Start date, transition period, checkpoints.')],
                    ] as const).map(([label, val, setter, ph]) => (
                        <label key={label} className="filter-label" style={{ margin: 0 }}>
                            <span style={{ color: 'var(--ad-ink)', fontWeight: 700 }}>{label}</span>
                            <textarea value={val} onChange={(e) => (setter as (s: string) => void)(e.target.value)} rows={3} placeholder={ph} style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: '0.88rem', padding: 8, border: '1px solid rgba(var(--ad-soft-rgb),0.4)', borderRadius: 6 }} />
                        </label>
                    ))}
                </div>
            ) : (
                <div style={{ marginTop: 14 }}>
                    <RichTextEditor value={htmlBody} onChange={setHtmlBody} locale={locale === 'en' ? 'en' : 'hr'} minHeight={320} placeholder={t('Poštovani kolege…', 'Dear colleagues…')} />
                </div>
            )}

            {/* PRILOG */}
            <label className="filter-label" style={{ marginTop: 12 }}>
                📎 {t('PDF prilozi (opcionalno, može više)', 'PDF attachments (optional, multiple)')}
                <input type="file" accept="application/pdf" multiple onChange={(e) => { setAttachments((prev) => [...prev, ...Array.from(e.target.files ?? [])]); e.target.value = ''; }} style={{ display: 'block', marginTop: 4, fontSize: '0.82rem' }} />
                {attachments.length ? (
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
                        {attachments.map((f, i) => (
                            <span key={i} style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: '0.78rem', color: '#1f7a36' }}>
                                ✓ {f.name} ({Math.round(f.size / 1024)} KB)
                                <button type="button" onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))} style={{ border: 0, background: 'transparent', color: '#b14040', cursor: 'pointer' }}>✕</button>
                            </span>
                        ))}
                    </span>
                ) : existingAttachmentName ? (
                    <span style={{ display: 'block', marginTop: 4, fontSize: '0.78rem', color: 'var(--ad-ink)' }}>
                        📎 {t('Trenutni prilozi', 'Current attachments')}: <strong>{existingAttachmentName}</strong> — {t('ostaju osim ako dodaš nove PDF-ove.', 'kept unless you add new PDFs.')}
                    </span>
                ) : (
                    <span className="muted" style={{ fontSize: '0.72rem' }}>{t('Svi prilozi se spajaju s tijelom u jedan PDF (DocDoc) + pregled na read stranici.', 'All attachments merged with body into one PDF (DocDoc) + viewer on read page.')}</span>
                )}
            </label>
            {(attachments.length > 0 || existingAttachmentName) ? (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: '0.8rem', color: 'var(--ad-ink)' }}>
                    <input type="checkbox" checked={allowDownload} onChange={(e) => setAllowDownload(e.target.checked)} />
                    {t('Dopusti download priloga (default: samo pregled, bez skidanja)', 'Allow attachment download (default: view-only)')}
                </label>
            ) : null}

            {/* ODOBRENJE */}
            <div className="okr-panel">
                <div className="okr-panel-title">🔐 {t('Odobrenje (obavezno)', 'Approval (mandatory)')}</div>
                <p className="muted" style={{ fontSize: '0.74rem', margin: '4px 0 8px', lineHeight: 1.4 }}>
                    {t('Svako slanje ide na odobrenje. Grupe automatski traže odobrenje SVOG nadležnog. Šalje se tek kad SVI odobravatelji odobre.',
                       'Every send needs approval. Groups auto-require their designated approver. Sent only after ALL approvers approve.')}
                </p>
                <label style={{ display: 'block', fontSize: '0.8rem' }}>
                    {t('Odobravatelji (uključi barem jednog člana uprave — on je zadnji potpis):', 'Approvers (include at least one board member — final sign-off):')}
                    <MultiSelectDropdown
                        locale={locale === 'en' ? 'en' : 'hr'}
                        options={zaposlenici.map((z) => ({ value: z.id, label: `${z.full_name} (${z.email})${boardMembers.some((b) => b.id === z.id) ? ' — uprava' : ''}` }))}
                        selected={extraApproverIds}
                        onChange={(next) => setExtraApproverIds(next.map((v) => Number(v)))}
                        emptyLabel={t('Odaberi odobravatelje…', 'Select approvers…')}
                    />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', marginTop: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={goesToMp} onChange={(e) => setGoesToMp(e.target.checked)} />
                    {t('Ide na maloprodaju (MP) — Renata Kalinkov odobrava prva', 'Goes to retail (MP) — Renata approves first')}
                </label>
                <p className="muted" style={{ fontSize: '0.72rem', marginTop: 6, lineHeight: 1.5 }}>
                    🔗 {t('Redoslijed: ', 'Order: ')}<strong>{t('Ti', 'You')}{goesToMp ? ' → Renata' : ''}{extraApproverIds.length > 0 ? t(' → odobravatelji', ' → approvers') : ''}{t(' → Iva (skriveno)', ' → Iva (hidden)')} → {t('uprava (zadnji potpis)', 'board (final sign-off)')}</strong>
                    {'. '}{t('Sekvencijalno — svatko dobije mail tek kad prethodni odobri. Članovi uprave među odabranima potpisuju ZADNJI.', 'Sequential — each gets the mail only after the previous approves. Board members among the selected sign LAST.')}
                </p>
            </div>

            {result ? <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 6, background: resultErr ? 'rgba(177,64,64,0.1)' : 'rgba(31,122,54,0.1)', color: resultErr ? '#b14040' : '#1f7a36', fontSize: '0.84rem', fontWeight: resultErr ? 700 : 400 }}>{resultErr ? '⚠ ' : ''}{result}</div> : null}

            <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap', alignItems: 'stretch' }}>
                <button type="button" onClick={() => setShowPreview(true)} disabled={sending} className="secondary"
                    title={t('Pregledaj kako izgleda prije slanja', 'Preview before sending')}
                    style={{ padding: '10px 18px', borderRadius: 8, fontWeight: 700, border: '1.5px solid rgba(var(--ad-soft-rgb),0.6)', background: '#fffdf6', color: 'var(--ad-ink)', cursor: 'pointer' }}>
                    👁 {t('Pregled', 'Preview')}
                </button>
                <button type="button" onClick={() => void saveDraft()} disabled={sending} className="secondary"
                    title={draftId ? t('Spremi izmjene skice (uklj. primatelje)', 'Save draft changes (incl. recipients)') : t('Spremi kao skicu bez slanja — dovrši kasnije', 'Save as draft without sending — finish later')}
                    style={{ padding: '10px 18px', borderRadius: 8, fontWeight: 700, border: '1.5px solid rgba(var(--ad-soft-rgb),0.6)', background: '#fffdf6', color: 'var(--ad-ink)', cursor: 'pointer', flex: draftId ? 1 : undefined }}>
                    💾 {draftId ? t('Spremi izmjene', 'Save changes') : t('Spremi skicu', 'Save draft')}
                </button>
                {!draftId ? (
                    <button type="button" onClick={() => void send()} disabled={sending} className="okr-submit" style={{ flex: 1, minWidth: 220 }}>
                        {sending ? '⏳ ' + t('Šaljem…', 'Sending…') : `📋 ${t('Pošalji na odobrenje', 'Submit for approval')}`}
                    </button>
                ) : null}
            </div>

            {showPreview ? (
                <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={() => setShowPreview(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 12px', zIndex: 1000, overflowY: 'auto' }}>
                    <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(820px, 96vw)', background: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 20px 60px rgba(107,79,28,0.3)' }}>
                        <div style={{ background: 'linear-gradient(135deg,var(--ad-ink),var(--ad-acc))', color: '#fffbeb', padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <strong>👁 {t('Pregled okružnice (prije slanja)', 'Circular preview (before sending)')}</strong>
                            <button type="button" onClick={() => setShowPreview(false)} style={{ background: 'transparent', border: 0, color: '#fffbeb', fontSize: '1.6rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
                        </div>
                        <div style={{ padding: '18px 22px', maxHeight: '76vh', overflowY: 'auto' }}>
                            <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.74rem', color: 'var(--ad-acc-3)', fontWeight: 700 }}>OK-{new Date().getFullYear()}-{((fOdjel || 'OPCE').toUpperCase().replace(/[^A-Z0-9]/g, '') || 'OPCE').slice(0, 20)}-NNN <em>({t('broj se dodjeljuje pri spremanju', 'number assigned on save')})</em></div>
                            <h2 style={{ color: 'var(--ad-ink)', margin: '6px 0 12px' }}>{subject || t('(bez naslova)', '(no title)')}</h2>
                            {/* Preview koristi ISTU tipografiju kao editor (okr-rte-surface: Georgia, naslovi, p) → pisanje = pregled. Iva 2026-07-02. */}
                            <div className="okr-rte-surface" style={{ minHeight: 0, padding: 0, background: 'transparent' }} dangerouslySetInnerHTML={{ __html: (composeMode === 'form' ? buildHtmlFromForm() : htmlBody) || `<p><em style="color:#999">${t('(prazan sadržaj)', '(empty body)')}</em></p>` }} />
                            {attachments.length ? <p style={{ marginTop: 14, padding: '8px 12px', background: 'rgba(var(--ad-acc-rgb),0.1)', borderRadius: 8, fontSize: '0.84rem' }}>📎 {t('Prilozi', 'Attachments')}: <strong>{attachments.length}</strong> — {t('spajaju se u jedan PDF pri slanju.', 'merged into one PDF on send.')}</p> : null}
                            <div style={{ marginTop: 14, borderTop: '1px solid rgba(var(--ad-soft-rgb),0.3)', paddingTop: 10, fontSize: '0.82rem', color: '#555' }}>
                                👥 {t('Primatelji', 'Recipients')}: {recipientSummary}
                                {resolvedRecipients.length > 0 ? (
                                    <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: '0.8rem', lineHeight: 1.45 }}>
                                        {resolvedRecipients.map((r) => (
                                            <li key={r.email}>
                                                <strong>{r.name || r.email}</strong>
                                                {r.name ? ` (${r.email})` : ''}
                                                {recipientSourceText(r, locale) ? ` — ${recipientSourceText(r, locale)}` : ''}
                                            </li>
                                        ))}
                                    </ul>
                                ) : resolvedLoading ? <div className="muted" style={{ marginTop: 6 }}>{t('Učitavanje popisa…', 'Loading list…')}</div> : null}
                            </div>
                        </div>
                        <div style={{ padding: '12px 18px', borderTop: '1px solid rgba(var(--ad-soft-rgb),0.25)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button type="button" className="secondary" onClick={() => setShowPreview(false)}>{t('Zatvori', 'Close')}</button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function ExtendRecipientsPanel({ circularId, groups, locale, token, onClose, onDone }: {
    circularId: number;
    groups: Group[];
    locale: Locale;
    token: string;
    onClose: () => void;
    onDone: () => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [pickedGroupIds, setPickedGroupIds] = useState<Set<number>>(new Set());
    const [pickedUserIds, setPickedUserIds] = useState<Set<number>>(new Set());
    const [includeSubgroups, setIncludeSubgroups] = useState(true);
    const [preview, setPreview] = useState<ResolvedRecipientPreview[]>([]);
    const [skippedExisting, setSkippedExisting] = useState(0);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [users, setUsers] = useState<BiAdminUser[]>([]);

    useEffect(() => {
        api<{ users: BiAdminUser[] }>('/admin/users?scope=active', token)
            .then((d) => setUsers(d.users ?? []))
            .catch(() => setUsers([]));
    }, [token]);

    useEffect(() => {
        if (pickedGroupIds.size === 0 && pickedUserIds.size === 0) {
            setPreview([]);
            setSkippedExisting(0);
            return;
        }
        let cancelled = false;
        setPreviewLoading(true);
        api<{ rows: ResolvedRecipientPreview[]; skipped_existing?: number }>(`/circulars/${circularId}/preview-extend-recipients`, token, {
            method: 'POST',
            body: JSON.stringify({
                recipient_user_ids: Array.from(pickedUserIds),
                recipient_group_ids: Array.from(pickedGroupIds),
                include_subgroups: includeSubgroups,
            }),
        })
            .then((d) => {
                if (!cancelled) {
                    setPreview(d.rows ?? []);
                    setSkippedExisting(d.skipped_existing ?? 0);
                }
            })
            .catch(() => { if (!cancelled) { setPreview([]); setSkippedExisting(0); } })
            .finally(() => { if (!cancelled) setPreviewLoading(false); });
        return () => { cancelled = true; };
    }, [circularId, pickedGroupIds, pickedUserIds, includeSubgroups, token]);

    const toggleGroup = (id: number) => setPickedGroupIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const toggleUser = (id: number) => setPickedUserIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const submit = async () => {
        if (pickedGroupIds.size === 0 && pickedUserIds.size === 0) {
            alert(t('Odaberi barem 1 grupu ili korisnika.', 'Pick at least 1 group or user.'));
            return;
        }
        if (preview.length === 0) {
            alert(t('Nema novih primatelja — svi odabrani već su na listi.', 'No new recipients — all selected are already on the list.'));
            return;
        }
        if (!confirm(t(`Poslati okružnicu za ${preview.length} novih primatelja? Sadržaj je već odobren pa im ide ODMAH (bez ponovnog odobravanja).`, `Send the circular to ${preview.length} new recipients? Content is already approved, so it goes out immediately (no re-approval).`))) return;
        setBusy(true);
        try {
            const r = await mutate<{ new_recipient_count: number; sent?: number; failed?: number }>(`/circulars/${circularId}/extend-recipients`, token, 'POST', {
                recipient_user_ids: Array.from(pickedUserIds),
                recipient_group_ids: Array.from(pickedGroupIds),
                include_subgroups: includeSubgroups,
            });
            alert(t(`Dopuna poslana ODMAH — ${r.sent ?? r.new_recipient_count} novih primatelja${r.failed ? `, ${r.failed} neuspjelo` : ''} (bez odobrenja, sadržaj već odobren).`, `Extension sent immediately — ${r.sent ?? r.new_recipient_count} new recipients${r.failed ? `, ${r.failed} failed` : ''} (no approval, content already approved).`));
            onDone();
            onClose();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const childrenOf = (parent: number | null) => groups.filter((g) => (g.parent_group_id ?? null) === parent);
    const renderGroupTree = (parent: number | null, depth = 0): React.ReactNode[] =>
        childrenOf(parent).flatMap((g) => [
            <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', paddingLeft: 8 + depth * 14, cursor: 'pointer', background: pickedGroupIds.has(g.id) ? 'rgba(31,122,54,0.08)' : 'transparent', borderRadius: 4, fontSize: '0.82rem' }}>
                <input type="checkbox" checked={pickedGroupIds.has(g.id)} onChange={() => toggleGroup(g.id)} />
                <span>{g.name}</span>
                <span className="muted" style={{ fontSize: '0.72rem' }}>({g.direct_member_count})</span>
            </label>,
            ...renderGroupTree(g.id, depth + 1),
        ]);

    return (
        <div role="dialog" aria-modal="true" onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={(e) => e.stopPropagation()} className="okr-card" style={{ width: 'min(720px, 96vw)', maxHeight: '90vh', overflowY: 'auto', padding: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                        <h3 style={{ margin: 0, color: 'var(--ad-ink)' }}>➕ {t('Pošalji dodatnoj grupi', 'Send to additional group')}</h3>
                        <p className="muted" style={{ fontSize: '0.78rem', margin: '4px 0 0' }}>{t('Ista okružnica — mail odmah samo novim primateljima (bez odobrenja).', 'Same circular — email sent immediately to new recipients only (no approval).')}</p>
                    </div>
                    <button type="button" onClick={onClose} className="secondary compact">✕</button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                        <strong style={{ fontSize: '0.78rem', color: 'var(--ad-acc-3)' }}>{t('Grupe', 'Groups')}</strong>
                        <div style={{ marginTop: 6, maxHeight: 180, overflowY: 'auto', border: '1px solid rgba(var(--ad-soft-rgb),0.35)', borderRadius: 6, padding: 4 }}>
                            {groups.length === 0 ? <p className="muted" style={{ fontSize: '0.78rem', padding: 8 }}>{t('Nema grupa.', 'No groups.')}</p> : renderGroupTree(null)}
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', marginTop: 8 }}>
                            <input type="checkbox" checked={includeSubgroups} onChange={(e) => setIncludeSubgroups(e.target.checked)} />
                            {t('Uključi podgrupe', 'Include subgroups')}
                        </label>
                    </div>
                    <div>
                        <strong style={{ fontSize: '0.78rem', color: 'var(--ad-acc-3)' }}>{t('Pojedinci', 'Individuals')}</strong>
                        <div style={{ marginTop: 6, maxHeight: 180, overflowY: 'auto', border: '1px solid rgba(var(--ad-soft-rgb),0.35)', borderRadius: 6, padding: 4 }}>
                            {users.map((u) => (
                                <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', cursor: 'pointer', background: pickedUserIds.has(u.id) ? 'rgba(31,122,54,0.08)' : 'transparent', borderRadius: 4, fontSize: '0.82rem' }}>
                                    <input type="checkbox" checked={pickedUserIds.has(u.id)} onChange={() => toggleUser(u.id)} />
                                    <span>{u.name}</span>
                                    <span className="muted" style={{ fontSize: '0.72rem' }}>{u.email}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div style={{ marginTop: 14 }}>
                    <strong style={{ fontSize: '0.78rem', color: 'var(--ad-acc-3)' }}>{t('Novi primatelji (preview)', 'New recipients (preview)')}</strong>
                    {previewLoading ? <p className="muted" style={{ fontSize: '0.78rem' }}>{t('Učitavam…', 'Loading…')}</p> : null}
                    {!previewLoading && preview.length === 0 && (pickedGroupIds.size > 0 || pickedUserIds.size > 0) ? (
                        <p className="muted" style={{ fontSize: '0.78rem' }}>{t('Nema novih — svi već primaju ovu okružnicu.', 'None new — all already receive this circular.')}{skippedExisting > 0 ? ` (${skippedExisting} ${t('preskočeno', 'skipped')})` : ''}</p>
                    ) : null}
                    {preview.length > 0 ? (
                        <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: '0.8rem', maxHeight: 140, overflowY: 'auto' }}>
                            {preview.map((r) => (
                                <li key={r.email}>
                                    {r.name ?? r.email}
                                    {r.name ? <span className="muted"> ({r.email})</span> : null}
                                    {recipientSourceText(r, locale) ? <span style={{ color: 'var(--ad-acc-3)', fontSize: '0.85em' }}> — {recipientSourceText(r, locale)}</span> : null}
                                </li>
                            ))}
                        </ul>
                    ) : null}
                    {skippedExisting > 0 && preview.length > 0 ? (
                        <p className="muted" style={{ fontSize: '0.72rem', marginTop: 4 }}>{t(`${skippedExisting} već na listi (neće dobiti mail).`, `${skippedExisting} already on list (won't receive email).`)}</p>
                    ) : null}
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
                    <button type="button" onClick={onClose} className="secondary compact">{t('Odustani', 'Cancel')}</button>
                    <button type="button" disabled={busy || preview.length === 0} onClick={() => void submit()} style={{ background: '#1f7a36', color: '#fff', border: 0, padding: '8px 16px', borderRadius: 6, fontWeight: 700, cursor: 'pointer' }}>
                        📤 {t('Pošalji novima', 'Send to new')}
                    </button>
                </div>
            </div>
        </div>
    );
}

function CircularDetailDrawer({ detail, groups, locale, token, mayCompose = false, onClose, onReply, onEditDraft, onOpenThread, onChanged, onOpenId }: {
    detail: CircularDetail | null;
    groups: Group[];
    locale: Locale;
    token: string;
    mayCompose?: boolean;
    onClose: () => void;
    onReply: (parentId: number, subject: string) => void;
    onEditDraft: (id: number) => void;
    onOpenThread: (id: number) => void;
    onChanged: () => void;
    onOpenId: (id: number) => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [busy, setBusy] = useState(false);
    const [remindMsg, setRemindMsg] = useState<string | null>(null);
    const [editing, setEditing] = useState(false);
    const [editSubject, setEditSubject] = useState('');
    const [editBody, setEditBody] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [deleteThread, setDeleteThread] = useState(false);
    const [pdfModalUrl, setPdfModalUrl] = useState<string | null>(null);
    const [showExtend, setShowExtend] = useState(false);

    const canEdit = Boolean(detail?.permissions?.can_edit);
    const canManageApprovers = Boolean(detail?.permissions?.can_manage_approvers);
    // Običan primatelj (ne kreator/manager/superadmin OVE okružnice) → ČISTI prikaz: naslov + tijelo + prilog.
    // Bez administratorske krome (verzije, brisanje, metrike primatelja/otvaranja, lanac odobravanja, dnevnik, popis primatelja).
    const canManage = canEdit || canManageApprovers;

    /** Najnoviji token po odobravatelju (re-submit stvara više redova). */
    const latestApprovers = (() => {
        const map = new Map<string, NonNullable<CircularDetail['approvers']>[number]>();
        for (const a of detail?.approvers ?? []) {
            const key = a.approver_zaposlenik_id != null ? `z${a.approver_zaposlenik_id}` : `e${a.approver_email ?? ''}`;
            map.set(key, a);
        }
        return Array.from(map.values());
    })();

    const openCombinedPdf = async () => {
        if (!detail) return;
        try {
            const response = await fetch(`${API_BASE}/circulars/${detail.circular.id}/combined-pdf`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!response.ok) throw new Error(String(response.status));
            const blob = await response.blob();
            setPdfModalUrl(URL.createObjectURL(blob));
        } catch (e) {
            alert(e instanceof Error ? e.message : 'PDF');
        }
    };

    const openRecipientPreview = async () => {
        if (!detail) return;
        try {
            const { url } = await api<{ url: string }>(`/circulars/${detail.circular.id}/preview-link`, token);
            window.open(url, '_blank', 'noopener');
        } catch (e) {
            alert(e instanceof Error ? e.message : t('Greška pri otvaranju pregleda.', 'Failed to open preview.'));
        }
    };

    useEffect(() => () => { if (pdfModalUrl) URL.revokeObjectURL(pdfModalUrl); }, [pdfModalUrl]);

    const startEdit = () => {
        if (!detail) return;
        setEditSubject(detail.circular.subject);
        setEditBody(detail.circular.html_body);
        setEditing(true);
    };
    const saveEdit = async () => {
        if (!detail) return;
        if (!editSubject.trim() || !editBody.trim()) { alert(t('Naslov i sadržaj su obavezni.', 'Subject and body required.')); return; }
        setBusy(true);
        try {
            const r = await mutate<{ needs_resubmit?: boolean }>(`/circulars/${detail.circular.id}`, token, 'PATCH', { subject: editSubject, html_body: editBody });
            setEditing(false);
            // Izmjena sadržaja poništava staro odobrenje — ponudi povratak u krug odmah,
            // inače okružnica ostane visjeti dok se netko ne sjeti kliknuti "Ponovno na odobrenje".
            if (r?.needs_resubmit && confirm(t('Sadržaj je izmijenjen pa prethodno odobrenje više ne vrijedi.\n\nPoslati odmah na (ponovno) odobrenje?', 'Content changed so the previous approval no longer applies.\n\nSubmit for (re)approval now?'))) {
                try { await mutate(`/circulars/${detail.circular.id}/submit`, token, 'POST', {}); }
                catch (e) { alert(e instanceof Error ? e.message : 'API'); }
            }
            onChanged(); onOpenId(detail.circular.id);
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const remind = async () => {
        if (!detail) return;
        const unread = (detail.circular.recipient_count ?? 0) - (detail.circular.opened_count ?? 0);
        if (!confirm(t(`Poslati podsjetnik za ${unread} nepročitanih primatelja?`, `Send reminder to ${unread} unread recipients?`))) return;
        setBusy(true); setRemindMsg(null);
        try {
            const r = await mutate<{ reminded: number; failed: number; total_unread: number }>(`/circulars/${detail.circular.id}/remind`, token, 'POST', {});
            setRemindMsg(t(`Podsjećeno: ${r.reminded}, neuspjelo: ${r.failed}.`, `Reminded: ${r.reminded}, failed: ${r.failed}.`));
        } catch (e) { setRemindMsg(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const newVersion = async () => {
        if (!detail) return;
        if (!confirm(t('Kreirati novu verziju ove okružnice? (postojeća postaje starija verzija)', 'Create new version?'))) return;
        setBusy(true);
        try {
            const r = await mutate<{ ok: boolean; id: number; version_number: number }>(`/circulars/${detail.circular.id}/new-version`, token, 'POST', {});
            onChanged();
            onOpenId(r.id); // otvori novu verziju (draft) za uređivanje
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const sendApproved = async () => {
        if (!detail) return;
        const isExtension = detail.circular.status === 'sent';
        const pending = detail.pending_recipient_count ?? 0;
        const msg = isExtension
            ? t(`Poslati odobrenu dopunu ${pending} novim primateljima?`, `Send approved extension to ${pending} new recipients?`)
            : t('Poslati odobrenu okružnicu svim primateljima?', 'Send approved circular to all recipients?');
        if (!confirm(msg)) return;
        setBusy(true);
        try {
            const r = await mutate<{ sent: number; failed: number }>(`/circulars/${detail.circular.id}/send`, token, 'POST', {});
            alert(t(`Poslano: ${r.sent}, neuspjelo: ${r.failed}.`, `Sent: ${r.sent}, failed: ${r.failed}.`));
            onChanged(); onOpenId(detail.circular.id);
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const nudge = async () => {
        if (!detail) return;
        setBusy(true);
        try {
            const r = await mutate<{ ok: boolean; nudged?: string }>(`/circulars/${detail.circular.id}/nudge`, token, 'POST', {});
            alert(r.ok ? t(`Podsjetnik poslan trenutnom odobravatelju.`, 'Reminder sent to current approver.') : t('Podsjetnik nije poslan (provjeri email).', 'Reminder not sent.'));
            onChanged(); onOpenId(detail.circular.id);
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const resubmit = async () => {
        if (!detail) return;
        if (!confirm(t('Poslati na odobrenje nadležnima grupa?', 'Submit for approval?'))) return;
        setBusy(true);
        try {
            const r = await mutate<{ approver_count: number; error?: string }>(`/circulars/${detail.circular.id}/submit`, token, 'POST', {});
            alert(t(`Poslano na odobrenje — ${r.approver_count} odobravatelja.`, `Submitted — ${r.approver_count} approvers.`));
            onChanged(); onOpenId(detail.circular.id);
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const resendApproverMails = async () => {
        if (!detail) return;
        if (!confirm(t('Ponovno poslati mail odobravateljima koji još nisu reagirali?', 'Resend approval mail to approvers who haven\'t responded?'))) return;
        setBusy(true);
        try {
            const r = await mutate<{ resent: number; failed: number; note?: string; results: Array<{ email: string; sent: boolean; note?: string }> }>(`/circulars/${detail.circular.id}/resend-approver-mails`, token, 'POST', {});
            if (r.note) { alert(r.note); }
            else { alert(t(`Ponovno poslano: ${r.resent}${r.failed ? `, neuspjelo: ${r.failed}` : ''}.`, `Resent: ${r.resent}${r.failed ? `, failed: ${r.failed}` : ''}.`)); }
            onChanged(); onOpenId(detail.circular.id);
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const removeApprover = async (zaposlenikId: number, name: string) => {
        if (!detail) return;
        if (!confirm(t(`Ukloniti ${name} iz kruga odobravanja? (npr. nema pristup računalu)`, `Remove ${name} from approval?`))) return;
        setBusy(true);
        try {
            const r = await mutate<{ ok: boolean; removed: Array<{ name: string }>; approval_state?: { state?: string; dispatched?: boolean; sent?: number } }>(
                `/circulars/${detail.circular.id}/remove-approvers`, token, 'POST', { approver_zaposlenik_ids: [zaposlenikId] },
            );
            const st = r.approval_state?.state;
            if (st === 'approved_sent') {
                alert(t(`Uklonjeno. Okružnica je poslana (${r.approval_state?.sent ?? 0} primatelja).`, `Removed. Circular sent (${r.approval_state?.sent ?? 0} recipients).`));
            } else {
                alert(t(`Uklonjeno: ${r.removed.map((x) => x.name).join(', ')}`, `Removed: ${r.removed.map((x) => x.name).join(', ')}`));
            }
            onChanged(); onOpenId(detail.circular.id);
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const del = async () => {
        if (!detail) return;
        setBusy(true);
        try {
            await mutate(`/circulars/${detail.circular.id}`, token, 'DELETE', { scope: deleteThread ? 'thread' : 'version' });
            setDeleteConfirm(false);
            onChanged();
            onClose();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    return (
        <div role="dialog" aria-modal="true" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(880px, 96vw)', height: '100vh', overflowY: 'auto', background: 'linear-gradient(180deg, #fffdf6 0%, var(--ad-bg) 100%)' }}>
                <div style={{ background: 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))', padding: '14px 20px', color: '#fffbeb', position: 'sticky', top: 0, zIndex: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <p style={{ margin: 0, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.14em', opacity: 0.75 }}>{t('Okružnica', 'Circular')} #{detail?.circular.id ?? '—'}</p>
                        <h2 style={{ margin: '0.2rem 0 0', fontSize: '1.15rem', fontWeight: 700 }}>{detail?.circular.subject ?? '—'}</h2>
                        {detail?.circular.message_id ? (
                            <p style={{ margin: '4px 0 0', fontSize: '0.7rem', opacity: 0.7, fontFamily: 'ui-monospace, monospace' }}>{detail.circular.message_id}</p>
                        ) : null}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                        {detail && mayCompose ? (
                            <button type="button" onClick={() => onReply(detail.circular.id, detail.circular.subject)} style={{ background: 'rgba(31,122,54,0.8)', color: '#fff', border: 0, padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>
                                💬 {t('Odgovori', 'Reply')}
                            </button>
                        ) : null}
                        {detail && canManage ? (
                            <>
                                <button type="button" disabled={busy} onClick={() => void newVersion()} title={t('Nova verzija (v+1)', 'New version')} style={{ background: 'rgba(255,255,255,0.18)', color: '#fff', border: 0, padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>
                                    🆕 {t('Nova verzija', 'New version')}
                                </button>
                                <button type="button" disabled={busy} onClick={() => { setDeleteThread(false); setDeleteConfirm(true); }} title={t('Obriši', 'Delete')} style={{ background: 'rgba(177,64,64,0.85)', color: '#fff', border: 0, padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>
                                    🗑
                                </button>
                            </>
                        ) : null}
                        <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: 0, padding: '4px 12px', borderRadius: 6, cursor: 'pointer', fontSize: '1.05rem' }}>✕</button>
                    </div>
                </div>
                {!detail ? <p className="muted" style={{ padding: 20 }}>{t('Učitavam…', 'Loading…')}</p> : (
                    <div style={{ padding: '14px 22px' }}>
                        {deleteConfirm ? (
                            <div style={{ marginBottom: 14, padding: '12px 14px', background: 'rgba(177,64,64,0.08)', border: '1px solid rgba(177,64,64,0.4)', borderRadius: 8 }}>
                                <strong style={{ color: '#b14040' }}>🗑 {t('Obrisati okružnicu?', 'Delete circular?')}</strong>
                                <p className="muted" style={{ fontSize: '0.78rem', margin: '4px 0 8px' }}>{t('Soft-delete — superadmin može vratiti iz "Smeće".', 'Soft delete — superadmin can restore from "Trash".')}</p>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.84rem', marginBottom: 10 }}>
                                    <input type="checkbox" checked={deleteThread} onChange={(e) => setDeleteThread(e.target.checked)} />
                                    {t('Obriši cijeli thread (sve verzije)', 'Delete whole thread (all versions)')}
                                </label>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button type="button" disabled={busy} onClick={() => void del()} style={{ background: '#b14040', color: '#fff', border: 0, padding: '6px 14px', borderRadius: 6, fontWeight: 700, cursor: 'pointer' }}>
                                        {t(deleteThread ? 'Obriši cijeli thread' : 'Obriši ovu verziju', deleteThread ? 'Delete thread' : 'Delete this version')}
                                    </button>
                                    <button type="button" onClick={() => setDeleteConfirm(false)} className="secondary compact">{t('Odustani', 'Cancel')}</button>
                                </div>
                            </div>
                        ) : null}
                        {canManage ? (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 14 }}>
                                <Metric label={t('Status', 'Status')} value={detail.circular.status} />
                                <Metric label={t('Primatelji', 'Recipients')} value={String(detail.circular.recipient_count)} />
                                <Metric label={t('Otvoreno', 'Opened')} value={`${detail.circular.opened_count} / ${detail.circular.recipient_count}`} />
                                <Metric label={t('Poslano', 'Sent')} value={detail.circular.sent_at ? formatDate(detail.circular.sent_at, locale) : '—'} />
                            </div>
                        ) : (
                            <p className="muted" style={{ fontSize: '0.8rem', margin: '0 0 12px' }}>{detail.circular.sent_at ? `📨 ${t('Poslano', 'Sent')}: ${formatDate(detail.circular.sent_at, locale)}` : ''}</p>
                        )}

                        {/* Odobravanje: status + progres + akcije + dnevnik — SAMO kreator/manager/superadmin. */}
                        {canManage && (detail.circular.status !== 'sent' || detail.circular.approval_status === 'pending' || Number(detail.circular.ready_to_send) === 1 || (detail.events?.length ?? 0) > 0) ? (() => {
                            const aStatus = detail.circular.approval_status;
                            const isExtensionPending = detail.circular.status === 'sent' && aStatus === 'pending';
                            // PDO/MySQL vrati tinyint kao string "1"/"0" pa coerce-amo (=== 1 ne bi prošao)
                            const ready = Number(detail.circular.ready_to_send) === 1;
                            const statusBadge = isExtensionPending ? { bg: 'rgba(138,104,23,0.15)', c: 'var(--ad-acc-3)', tx: t('Dopuna čeka odobrenje', 'Extension pending approval') }
                                : aStatus === 'approved' ? { bg: 'rgba(31,122,54,0.15)', c: '#1f7a36', tx: ready ? (detail.circular.status === 'sent' ? t('Dopuna odobrena — čeka slanje', 'Extension approved — awaiting send') : t('Odobreno — čeka tvoje slanje', 'Approved — awaiting your send')) : t('Odobreno', 'Approved') }
                                : aStatus === 'rejected' ? { bg: 'rgba(177,64,64,0.15)', c: '#b14040', tx: t('Odbačeno', 'Rejected') }
                                : aStatus === 'changes_requested' ? { bg: 'rgba(138,104,23,0.15)', c: 'var(--ad-acc-3)', tx: t('Tražene izmjene', 'Changes requested') }
                                : aStatus === 'draft' ? { bg: 'rgba(120,113,90,0.15)', c: '#6b5f3a', tx: '💾 ' + t('Skica (nije poslana na odobrenje)', 'Draft (not submitted)') }
                                : { bg: 'rgba(138,104,23,0.12)', c: 'var(--ad-acc-3)', tx: t('Čeka odobrenje', 'Pending approval') };
                            const actionLabel: Record<string, string> = { approve_send: '✓ '+t('Odobreno i poslano','Approved & sent'), approve_return: '✓ '+t('Odobreno (vraćeno)','Approved (returned)'), changes: '✎ '+t('Tražene izmjene','Changes'), reject: '✗ '+t('Odbačeno','Rejected'), resubmit: '↻ '+t('Ponovno na odobrenje','Resubmitted'), sent: '📤 '+t('Poslano','Sent'), extension_sent: '📤 '+t('Dopuna poslana','Extension sent'), extend_recipients: '➕ '+t('Dopuna primatelja','Recipient extension'), edited: '✎ '+t('Uređeno','Edited'), approver_mail_resent: '📧 '+t('Mail odobravateljima ponovno poslan','Approver mail resent'), approver_removed: '⊘ '+t('Odobravatelj uklonjen','Approver removed'), created: '➕ '+t('Kreirano','Created'), approval_invalidated: '⚠ '+t('Odobrenje poništeno (sadržaj izmijenjen)','Approval invalidated (content changed)') };
                            return (
                                <div style={{ margin: '4px 0 14px', padding: '10px 12px', background: 'rgba(var(--ad-soft-rgb),0.06)', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 8 }}>
                                    {isExtensionPending && (detail.pending_recipient_count ?? 0) > 0 ? (
                                        <p style={{ margin: '0 0 8px', fontSize: '0.78rem', color: 'var(--ad-acc-3)' }}>
                                            {t(`Dopuna: ${detail.pending_recipient_count} novih primatelja čeka odobrenje (postojeći neće ponovno primiti mail).`, `Extension: ${detail.pending_recipient_count} new recipients await approval (existing won't receive email again).`)}
                                        </p>
                                    ) : null}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <span style={{ background: statusBadge.bg, color: statusBadge.c, padding: '3px 10px', borderRadius: 8, fontSize: '0.76rem', fontWeight: 700 }}>{statusBadge.tx}</span>
                                        {aStatus === 'approved' && ready && canEdit ? <button type="button" disabled={busy} onClick={() => void sendApproved()} style={{ background: '#1f7a36', color: '#fff', border: 0, padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem' }}>📤 {detail.circular.status === 'sent' ? t('Pošalji novima', 'Send to new') : t('Pošalji sad', 'Send now')}</button> : null}
                                        {detail.circular.status !== 'sent' && detail?.circular?.id && canEdit ? <button type="button" onClick={() => onEditDraft(Number(detail.circular.id))} style={{ background: '#fffdf6', color: 'var(--ad-ink)', border: '1.5px solid rgba(var(--ad-soft-rgb),0.6)', padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem' }}>✏️ {t('Uredi sve (primatelji + prilog + meta)', 'Edit all (recipients + attachment + meta)')}</button> : null}
                                        {aStatus === 'draft' && canEdit ? <button type="button" disabled={busy} onClick={() => void resubmit()} style={{ background: 'var(--ad-acc-3)', color: '#fff', border: 0, padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem' }}>📋 {t('Pošalji na odobrenje', 'Submit for approval')}</button> : null}
                                        {(aStatus === 'changes_requested' || aStatus === 'rejected') && canEdit ? <button type="button" disabled={busy} onClick={() => void resubmit()} style={{ background: 'var(--ad-acc-3)', color: '#fff', border: 0, padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem' }}>↻ {t('Ponovno na odobrenje', 'Resubmit')}</button> : null}
                                        {aStatus === 'pending' && canEdit && latestApprovers.some((a) => a.status === 'active') ? <button type="button" disabled={busy} onClick={() => void resendApproverMails()} title={t('Mail nije stigao? Pošalji ponovno odobravateljima koji čekaju.', 'Mail didn\'t arrive? Resend to waiting approvers.')} style={{ background: '#fffdf6', color: 'var(--ad-acc-3)', border: '1.5px solid rgba(138,104,23,0.5)', padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem' }}>📧 {t('Pošalji mail ponovno', 'Resend mail')}</button> : null}
                                        {aStatus === 'pending' && canEdit && latestApprovers.some((a) => a.status === 'active') ? <button type="button" disabled={busy} onClick={() => void nudge()} title={t('Gurni trenutnog odobravatelja koji kasni (ponovno mu pošalji podsjetnik).', 'Nudge the current approver who is late.')} style={{ background: 'var(--ad-acc-3)', color: '#fff', border: 0, padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem' }}>👉 {t('Požuri odobravatelja', 'Nudge approver')}</button> : null}
                                    </div>
                                    {(latestApprovers.length > 0) ? (
                                        <div style={{ marginTop: 8, fontSize: '0.78rem' }}>
                                            <strong style={{ color: 'var(--ad-ink)' }}>{t('Odobravatelji (svi moraju odobriti):', 'Approvers (all must approve):')}</strong>
                                            <ul style={{ margin: '4px 0 0', paddingLeft: 18, listStyle: 'none' }}>
                                                {latestApprovers.map((a, i) => {
                                                    const responded = a.status === 'used';
                                                    const waiting = a.status === 'active';
                                                    const icon = responded ? (a.used_action === 'reject' ? '✗' : a.used_action === 'changes' ? '✎' : '✓') : waiting ? '⏳' : '·';
                                                    const color = responded ? (a.used_action === 'reject' ? '#b14040' : '#1f7a36') : waiting ? 'var(--ad-acc-3)' : '#999';
                                                    const name = a.approver_name ?? a.approver_email ?? '—';
                                                    return (
                                                        <li key={i} style={{ padding: '2px 0', color, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                            <span style={{ fontWeight: 700, marginRight: 4 }}>{icon}</span>
                                                            {/* 'expired' bez odgovora = krug je prekinut ranije (netko prije je tražio izmjene/odbio),
                                                                a ne "istekao link" — sirovi status je zbunjivao pošiljatelje. */}
                                                            <span>{name} — <strong>{responded ? (actionLabel[a.used_action ?? ''] ?? a.used_action)
                                                                : waiting ? t('mail poslan, čeka odgovor', 'mail sent, awaiting')
                                                                : a.status === 'queued' ? t('na redu (čeka prethodne)', 'queued (waiting for earlier levels)')
                                                                : a.status === 'expired' ? t('krug prekinut — nije stigao na red', 'round interrupted — never reached')
                                                                : a.status}</strong></span>
                                                            {canManageApprovers && waiting && a.approver_zaposlenik_id ? (
                                                                <button type="button" disabled={busy} onClick={() => void removeApprover(Number(a.approver_zaposlenik_id), name)}
                                                                    title={t('Ukloni iz odobravanja', 'Remove from approval')}
                                                                    style={{ background: 'transparent', border: '1px solid rgba(177,64,64,0.5)', color: '#b14040', padding: '1px 8px', borderRadius: 4, fontSize: '0.68rem', cursor: 'pointer', fontWeight: 700 }}>
                                                                    ⊘ {t('Ukloni', 'Remove')}
                                                                </button>
                                                            ) : null}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    ) : null}
                                    {/* AUDIT TIMELINE (v349.2) — jasna kronologija: tko / kad / što. Spaja events + odobravatelje. */}
                                    {(() => {
                                        const fmtDT = (s: string | null | undefined): string => {
                                            if (!s) return '';
                                            const d = new Date(String(s).replace(' ', 'T'));
                                            if (isNaN(d.getTime())) return formatDate(s, locale);
                                            const pad = (n: number) => String(n).padStart(2, '0');
                                            return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}. ${pad(d.getHours())}:${pad(d.getMinutes())}`;
                                        };
                                        type TL = { ts: string | null; icon: string; color: string; label: string; who: string; comment?: string | null };
                                        const items: TL[] = [];
                                        // 1. Kreirano
                                        if (detail.circular.created_at) items.push({ ts: detail.circular.created_at, icon: '➕', color: '#6b5f3a', label: t('Kreirana okružnica', 'Circular created'), who: (detail.thread?.[0]?.sender_name ?? detail.thread?.[0]?.sender_email ?? '') as string });
                                        // 2. Eventi (uključ. submit/resend/approve/reject/sent)
                                        const evIcon: Record<string, [string, string]> = {
                                            edited: ['✎', 'var(--ad-acc-3)'], resubmit: ['📋', 'var(--ad-acc-3)'], approver_mail_resent: ['📧', 'var(--ad-acc-3)'], approval_invalidated: ['⚠', '#b14040'],
                                            approve_send: ['✓', '#1f7a36'], approve_return: ['✓', '#1f7a36'], changes: ['✎', 'var(--ad-acc-3)'], reject: ['✗', '#b14040'], sent: ['📤', '#1f7a36'], created: ['➕', '#6b5f3a'],
                                        };
                                        (detail.events ?? []).forEach((e) => {
                                            const [ic, col] = evIcon[e.action] ?? ['•', '#888'];
                                            items.push({ ts: e.created_at, icon: ic, color: col, label: (actionLabel[e.action] ?? e.action), who: e.actor_name ?? '—', comment: e.comment });
                                        });
                                        // 3. Odobravatelji koji su reagirali (s vremenom) — jasno tko je kada odobrio/odbio
                                        (detail.approvers ?? []).forEach((a) => {
                                            if (a.status === 'used' && a.used_at) {
                                                const isRej = a.used_action === 'reject';
                                                items.push({ ts: a.used_at, icon: isRej ? '✗' : a.used_action === 'changes' ? '✎' : '✓', color: isRej ? '#b14040' : '#1f7a36', label: (isRej ? t('Odbio', 'Rejected') : a.used_action === 'changes' ? t('Tražio izmjene', 'Requested changes') : t('Odobrio', 'Approved')), who: a.approver_name ?? a.approver_email ?? '—' });
                                            }
                                        });
                                        // Sort kronološki
                                        items.sort((x, y) => (new Date(String(x.ts ?? 0).replace(' ', 'T')).getTime()) - (new Date(String(y.ts ?? 0).replace(' ', 'T')).getTime()));
                                        if (items.length === 0) return null;
                                        return (
                                            <div style={{ marginTop: 12 }}>
                                                <strong style={{ color: 'var(--ad-ink)', fontSize: '0.78rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>🕓 {t('Tijek (tko / kad / što)', 'Trace (who / when / what)')}</strong>
                                                <div style={{ marginTop: 8, position: 'relative', paddingLeft: 22 }}>
                                                    {/* vertikalna linija */}
                                                    <div style={{ position: 'absolute', left: 7, top: 4, bottom: 4, width: 2, background: 'rgba(var(--ad-soft-rgb),0.35)' }} />
                                                    {items.map((it, i) => (
                                                        <div key={i} style={{ position: 'relative', paddingBottom: i === items.length - 1 ? 0 : 12 }}>
                                                            <div style={{ position: 'absolute', left: -19, top: 1, width: 16, height: 16, borderRadius: '50%', background: '#fff', border: `2px solid ${it.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem' }}>{it.icon}</div>
                                                            <div style={{ fontSize: '0.8rem' }}>
                                                                <strong style={{ color: it.color }}>{it.label}</strong>
                                                                {it.who ? <span> — {it.who}</span> : null}
                                                            </div>
                                                            <div style={{ fontSize: '0.7rem', color: '#999' }}>{fmtDT(it.ts)}</div>
                                                            {it.comment ? <div style={{ marginTop: 2, fontSize: '0.76rem', fontStyle: 'italic', background: 'rgba(var(--ad-soft-rgb),0.08)', padding: '4px 8px', borderRadius: 6, borderLeft: '2px solid var(--ad-acc)' }}>„{it.comment}"</div> : null}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            );
                        })() : null}

                        {detail.thread.length > 1 ? (
                            <>
                                <h4 style={{ margin: '14px 0 6px', fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>
                                    💬 {t('Thread', 'Thread')} ({detail.thread.length})
                                </h4>
                                <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 8, padding: 8, marginBottom: 12 }}>
                                    {detail.thread.map((tEntry) => (
                                        <div key={tEntry.id} onClick={() => onOpenThread(tEntry.id)} style={{ padding: '6px 10px', borderLeft: tEntry.id === detail.circular.id ? '3px solid var(--ad-acc)' : '3px solid transparent', cursor: tEntry.id === detail.circular.id ? 'default' : 'pointer', background: tEntry.id === detail.circular.id ? 'rgba(var(--ad-acc-rgb),0.08)' : 'transparent', borderRadius: 4 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                                <div>
                                                    <strong style={{ fontSize: '0.86rem' }}>{tEntry.inbound_source === 'imap_reply' ? '📩 ' : '✉️ '}{tEntry.subject}</strong>
                                                    <span className="muted" style={{ marginLeft: 6, fontSize: '0.74rem' }}>· {tEntry.sender_name ?? tEntry.sender_email ?? '—'}</span>
                                                </div>
                                                <span className="muted" style={{ fontSize: '0.74rem' }}>{tEntry.sent_at ? formatDate(tEntry.sent_at, locale) : '—'}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : null}

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '14px 0 6px' }}>
                            <h4 style={{ margin: 0, fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>
                                {t('Sadržaj', 'Body')}
                            </h4>
                            {detail.circular.status !== 'sent' && !editing && canEdit ? (
                                <button type="button" onClick={startEdit} className="secondary compact" style={{ fontSize: '0.74rem' }} title={t('Brza izmjena samo naslova i teksta. Za primatelje/prilog koristi "Uredi sve" gore.', 'Quick edit of subject + body only. For recipients/attachment use "Edit all" above.')}>✏️ {t('Brza izmjena teksta', 'Quick text edit')}</button>
                            ) : null}
                        </div>
                        {editing ? (
                            <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.4)', borderRadius: 8, padding: 12 }}>
                                <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>{t('Naslov', 'Subject')}
                                    <input value={editSubject} onChange={(e) => setEditSubject(e.target.value)} style={{ width: '100%', marginTop: 3 }} />
                                </label>
                                <div style={{ fontSize: '0.78rem', fontWeight: 600, marginTop: 8, marginBottom: 3 }}>{t('Sadržaj', 'Body')}</div>
                                <RichTextEditor value={editBody} onChange={setEditBody} locale={locale === 'en' ? 'en' : 'hr'} minHeight={220} />
                                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                    <button type="button" disabled={busy} onClick={() => void saveEdit()} style={{ background: '#1f7a36', color: '#fff', border: 0, padding: '6px 14px', borderRadius: 6, fontWeight: 700, cursor: 'pointer' }}>💾 {t('Spremi', 'Save')}</button>
                                    <button type="button" onClick={() => setEditing(false)} className="secondary compact">{t('Odustani', 'Cancel')}</button>
                                </div>
                                <p className="muted" style={{ fontSize: '0.72rem', marginTop: 6 }}>{t('Nakon izmjene pošalji (ponovno) na odobrenje.', 'After editing, (re)submit for approval.')}</p>
                            </div>
                        ) : (
                            <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 8, padding: 14, fontSize: '0.88rem', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: detail.circular.html_body }} />
                        )}

                        {canManage && !editing ? (
                            <div style={{ marginTop: 12 }}>
                                <button type="button" onClick={() => void openRecipientPreview()}
                                    title={t('Otvori okružnicu točno onako kako je vide primatelji (ne evidentira čitanje)', 'Open the circular exactly as recipients see it (does not mark as read)')}
                                    style={{ background: 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))', color: '#fffbeb', border: 0, padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '0.84rem' }}>
                                    👁 {t('Vidi okružnicu (kako je vide primatelji)', 'View circular (as recipients see it)')}
                                </button>
                            </div>
                        ) : null}

                        {(detail.circular as any).attachment_name ? (
                            <div style={{ marginTop: 12, padding: '10px 12px', background: 'rgba(var(--ad-acc-rgb),0.08)', border: '1px solid rgba(var(--ad-soft-rgb),0.4)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                <button type="button" onClick={() => void openCombinedPdf()}
                                    style={{ background: 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))', color: '#fff', border: 0, padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem' }}>
                                    📄 {t('Pregledaj spojeni PDF (tijelo + prilog)', 'View combined PDF (body + attachment)')}
                                </button>
                                <button type="button" onClick={() => void downloadFile(`/circulars/${detail.circular.id}/attachment`, token, (detail.circular as any).attachment_name)}
                                    className="secondary compact" style={{ fontSize: '0.78rem' }}>
                                    📎 {t('Preuzmi prilog', 'Download attachment')}
                                </button>
                                {(detail.circular as any).docdoc_uuid ? <span className="muted" style={{ fontSize: '0.72rem' }}>{t('arhivirano na DocDoc', 'archived on DocDoc')} ({String((detail.circular as any).docdoc_uuid).slice(0, 8)}…)</span> : null}
                            </div>
                        ) : null}

                        {detail?.circular?.id ? <CircularQuizPanel circularId={Number(detail.circular.id)} token={token} locale={locale} /> : null}

                        {canManage && detail.recipients.length > 0 ? (
                            <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '18px 0 6px' }}>
                                    <h4 style={{ margin: 0, fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>
                                        {t('Primatelji i status čitanja', 'Recipients and read status')}
                                    </h4>
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                        {detail.circular.status === 'sent' && canEdit ? (
                                            <button type="button" disabled={busy} onClick={() => setShowExtend(true)}
                                                style={{ background: 'var(--ad-ink)', color: '#fff', border: 0, padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem' }}>
                                                ➕ {t('Pošalji dodatnoj grupi', 'Send to additional group')}
                                            </button>
                                        ) : null}
                                        {canEdit && (detail.circular.recipient_count ?? 0) - (detail.circular.opened_count ?? 0) > 0 ? (
                                            <button type="button" disabled={busy} onClick={() => void remind()}
                                                style={{ background: 'var(--ad-acc-3)', color: '#fff', border: 0, padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem' }}>
                                                🔔 {t(`Podsjeti nepročitane (${(detail.circular.recipient_count ?? 0) - (detail.circular.opened_count ?? 0)})`, `Remind unread (${(detail.circular.recipient_count ?? 0) - (detail.circular.opened_count ?? 0)})`)}
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                                {remindMsg ? <div style={{ marginBottom: 8, fontSize: '0.8rem', color: remindMsg.includes('euspjelo') || remindMsg.includes('ailed') ? 'var(--ad-acc-3)' : '#1f7a36' }}>{remindMsg}</div> : null}
                                {/* Iva: sažetak označenih grupa/sektora IZNAD liste — za brzu provjeru jesu li svi uključeni */}
                                {(() => {
                                    const groupNames = Array.from(new Set(detail.recipients.flatMap((r) => (r.source_groups ?? []).map((g) => g.name)).filter(Boolean))).sort((a, b) => a.localeCompare(b));
                                    const directCount = detail.recipients.filter((r) => (r.source_groups ?? []).length === 0).length;
                                    if (groupNames.length === 0 && directCount === 0) return null;
                                    return (
                                        <div style={{ marginBottom: 10, padding: '8px 12px', background: 'rgba(var(--ad-acc-rgb),0.08)', border: '1px solid rgba(var(--ad-soft-rgb),0.35)', borderRadius: 8, fontSize: '0.8rem', lineHeight: 1.9 }}>
                                            <strong style={{ color: 'var(--ad-acc-3)' }}>{t('Uključene grupe / sektori', 'Included groups / sectors')}:</strong>{' '}
                                            {groupNames.map((n) => (
                                                <span key={n} style={{ display: 'inline-block', background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.5)', borderRadius: 10, padding: '1px 10px', margin: '0 3px', fontWeight: 600, color: 'var(--ad-ink)' }}>{n}</span>
                                            ))}
                                            {directCount > 0 ? <span style={{ display: 'inline-block', background: '#fff', border: '1px dashed rgba(var(--ad-soft-rgb),0.55)', borderRadius: 10, padding: '1px 10px', margin: '0 3px', color: 'var(--ad-acc-3)' }}>{t('pojedinačno', 'individual')}: {directCount}</span> : null}
                                        </div>
                                    );
                                })()}
                                <div className="table-wrap">
                                    <table className="admin-user-table" style={{ fontSize: '0.82rem' }}>
                                        <thead>
                                            <tr>
                                                <th>{t('Korisnik', 'User')}</th>
                                                <th>Email</th>
                                                <th>{t('Izvor', 'Source')}</th>
                                                <th>{t('Slanje', 'Send')}</th>
                                                <th>{t('Otvoreno', 'Opened')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {detail.recipients.map((r) => (
                                                <tr key={r.id} style={detail.circular.status === 'sent' && r.status === 'pending' ? { background: 'rgba(var(--ad-acc-rgb),0.08)' } : undefined}>
                                                    <td>{r.user_name ?? <span className="muted">—</span>}</td>
                                                    <td>{r.email_address}<EmployeeReadsList reads={r.employee_reads} locale={locale} /></td>
                                                    <td style={{ fontSize: '0.78rem', color: 'var(--ad-acc-3)' }}>{recipientSourceText(r, locale) || <span className="muted">—</span>}</td>
                                                    <td>
                                                        <span style={{
                                                            background: r.status === 'sent' ? 'rgba(31,122,54,0.15)' : r.status === 'failed' ? 'rgba(177,64,64,0.15)' : 'rgba(var(--ad-acc-rgb),0.15)',
                                                            color: r.status === 'sent' ? '#1f7a36' : r.status === 'failed' ? '#b14040' : 'var(--ad-acc-3)',
                                                            padding: '2px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700,
                                                        }}>{r.status}</span>
                                                    </td>
                                                    <td>{r.opened_at ? <strong style={{ color: '#1f7a36' }}>✓ {formatDate(r.opened_at, locale)}</strong> : <span className="muted">—</span>}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : null}
                    </div>
                )}
            </div>
            {pdfModalUrl ? (
                <div role="dialog" aria-modal="true" onClick={() => { URL.revokeObjectURL(pdfModalUrl); setPdfModalUrl(null); }}
                    style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.85)', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', background: 'rgba(0,0,0,0.6)' }}>
                        <span style={{ color: '#fff', fontWeight: 700 }}>📄 {t('Pregled PDF-a', 'PDF preview')}</span>
                        <button type="button" onClick={() => { URL.revokeObjectURL(pdfModalUrl); setPdfModalUrl(null); }}
                            style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: 0, borderRadius: 5, cursor: 'pointer', padding: '6px 12px', fontSize: '16px' }}>✕</button>
                    </div>
                    <iframe title="PDF" src={pdfModalUrl} style={{ flex: 1, width: '100%', border: 0, background: '#fff' }} />
                </div>
            ) : null}
            {showExtend && detail ? (
                <ExtendRecipientsPanel
                    circularId={detail.circular.id}
                    groups={groups}
                    locale={locale}
                    token={token}
                    onClose={() => setShowExtend(false)}
                    onDone={() => { onChanged(); onOpenId(detail.circular.id); }}
                />
            ) : null}
        </div>
    );
}

function Metric({ label, value }: { label: string; value: string }) {
    return (
        <div style={{ background: '#fff', borderRadius: 8, padding: '8px 12px', border: '1px solid rgba(var(--ad-soft-rgb),0.25)' }}>
            <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', color: 'rgba(0,0,0,0.5)' }}>{label}</div>
            <strong style={{ fontSize: '0.92rem' }}>{value}</strong>
        </div>
    );
}
